// src/components/EditDriver.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  FaArrowLeft,
  FaBed,
  FaBolt,
  FaCalendarAlt,
  FaCamera,
  FaCheck,
  FaEnvelope,
  FaExclamationTriangle,
  FaEye,
  FaFileAlt,
  FaGlobe,
  FaHome,
  FaIdCard,
  FaLanguage,
  FaMapMarkerAlt,
  FaMoon,
  FaOilCan,
  FaPen,
  FaPhoneAlt,
  FaRoute,
  FaSave,
  FaSnowflake,
  FaStar,
  FaTruckLoading,
  FaUpload,
  FaUser,
  FaUsers,
} from "react-icons/fa";
import axios from "../axiosConfig";
import "../styles/EditDriver.css";
import { useTranslation } from "react-i18next";

import {
  mergeDriverPreviewData,
  primeDriverDetailsCache,
  readDriverDetailsCache,
  writeDriverDetailsCache,
} from "../utils/driverDetailsCache";

/* --------------------------- Helpers --------------------------- */

const countryOptions = [
  { label: "Austria", value: "43", code: "at" },
  { label: "Belgium", value: "32", code: "be" },
  { label: "Croatia", value: "385", code: "hr" },
  { label: "Czech Republic", value: "420", code: "cz" },
  { label: "Denmark", value: "45", code: "dk" },
  { label: "Estonia", value: "372", code: "ee" },
  { label: "Finland", value: "358", code: "fi" },
  { label: "France", value: "33", code: "fr" },
  { label: "Germany", value: "49", code: "de" },
  { label: "Hungary", value: "36", code: "hu" },
  { label: "Italy", value: "39", code: "it" },
  { label: "Netherlands", value: "31", code: "nl" },
  { label: "Norway", value: "47", code: "no" },
  { label: "Poland", value: "48", code: "pl" },
  { label: "Portugal", value: "351", code: "pt" },
  { label: "Spain", value: "34", code: "es" },
  { label: "Sweden", value: "46", code: "se" },
  { label: "Switzerland", value: "41", code: "ch" },
  { label: "United Kingdom", value: "44", code: "gb" },
];

const yesNoOptions = [
  { value: "true", labelKey: "common.yes", fallback: "Sim" },
  { value: "false", labelKey: "common.no", fallback: "Não" },
];

const yesNoUnsetOptions = [
  { value: "", labelKey: "common.unset", fallback: "" },
  ...yesNoOptions,
];

const adrOptions = [
  { value: "NAO", labelKey: "common.no", fallback: "Não" },
  { value: "SIM", labelKey: "common.yes", fallback: "Sim" },
];

const emptyDriver = {
  fullname: "",
  phonenumber: "",
  birth_date: "",
  email: "",

  address: "",
  postal_code: "",
  city: "",
  country: "",

  tax_id: "",
  tax_id_expiry: "",

  emergency_name: "",
  emergency_contact: "",
  emergency_family: "",

  driving_license_number: "",
  license_country: "",
  license_categories: [],

  tacho_card_number: "",
  driver_tacho_number: "",
  issue_date_tacho_card: "",
  expiry_date_tacho_card: "",

  home_base_start: "",
  home_base_ends: "",

  languages_spoken: [],
  work_availability: [],
  lanes_preference: "",
  contact_preferences: "",

  distance: "",
  weight: "",
  temperature: "",

  take_photos: false,
  gps_sharing: null,
  urgent_jobs: false,
  night_weekend_driving: false,
  sleep_in_truck: false,

  crane_hiab_license: false,
  tanker_experience: false,
  refrigerated_experience: false,
  special_transport_experience: false,

  adr_hazmar_certification: "NAO",
  expiry_date_adr_hazmat_certificate: "",

  profile_photo: null,
  driver_id_photo: [],
  driver_license_photo: [],
  tacho_card_photo: [],
  upload_photo_adr_hazmat_certificate: [],
};

const fmtDateForInput = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const API_BASE = (axios?.defaults?.baseURL || "").replace(/\/+$/, "");

const buildFileUrl = (p) => {
  if (!p) return "";

  if (typeof p !== "string") {
    try {
      return URL.createObjectURL(p);
    } catch {
      return "";
    }
  }

  if (/^(https?:|blob:|data:)/i.test(p)) return p;

  const clean = ("/" + p).replace(/\\/g, "/").replace(/\/{2,}/g, "/");
  return `${API_BASE}${clean}`;
};

function splitPhone(contactNumber) {
  if (!contactNumber || !String(contactNumber).startsWith("+")) {
    return {
      country: countryOptions.find((c) => c.code === "pt") || countryOptions[0],
      number: contactNumber ? String(contactNumber).replace(/[^\d]/g, "") : "",
    };
  }

  const digits = String(contactNumber).replace(/[^\d]/g, "");
  const match = [...countryOptions]
    .sort((a, b) => b.value.length - a.value.length)
    .find((opt) => digits.startsWith(opt.value));

  if (match) {
    return {
      country: match,
      number: digits.slice(match.value.length),
    };
  }

  return {
    country: countryOptions.find((c) => c.code === "pt") || countryOptions[0],
    number: digits,
  };
}

function pickUrl(v) {
  if (!v) return null;
  if (typeof v === "string") return v;
  if (typeof v === "object") return v.url || v.uri || null;
  return null;
}

function normalizeFileFieldArray(v) {
  if (!v) return [];
  const arr = Array.isArray(v) ? v : [v];
  const urls = arr.map((x) => pickUrl(x)).filter(Boolean);
  return urls.slice(0, 2);
}

function normalizeFileField(v) {
  if (!v) return null;

  if (Array.isArray(v)) {
    const first = v[0];
    return pickUrl(first) || null;
  }

  if (typeof v === "object") return pickUrl(v);
  if (typeof v === "string") return v;

  return null;
}

function getFileNameFromUrl(u) {
  if (!u) return "";
  if (typeof u !== "string") return "";
  return u.split("/").pop() || "";
}

function prettyNameFromUrl(u) {
  const name = getFileNameFromUrl(u) || "";

  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

function isPdfUrl(u) {
  return /\.pdf(\?|#|$)/i.test(String(u || ""));
}

function isImageUrl(u) {
  return /\.(png|jpe?g|webp|gif|bmp|svg)(\?|#|$)/i.test(String(u || ""));
}

function getExt(u) {
  const s = String(u || "");
  const clean = s.split("?")[0].split("#")[0];
  const ext = (clean.split(".").pop() || "").toLowerCase();

  return ext && ext !== clean ? ext : "";
}

function preloadImage(url) {
  if (!url) return;
  const s = String(url);
  if (isPdfUrl(s)) return;

  const img = new Image();
  img.src = s;
}

function clampProfileValue(
  value,
  min,
  max
) {
  return Math.min(
    max,
    Math.max(
      min,
      value
    )
  );
}

async function loadProfileImageForCanvas(
  src
) {
  let imageSrc =
    src;

  let temporaryObjectUrl =
    null;

  /*
   * Quando a imagem vem do backend através de outro
   * origin, tentamos primeiro transformá-la num blob
   * local.
   *
   * Isto evita problemas de canvas "tainted" ao
   * recortar uma foto já existente.
   */
  if (
    /^https?:/i.test(
      String(src || "")
    )
  ) {
    try {
      const response =
        await axios.get(
          src,
          {
            responseType:
              "blob",
          }
        );

      temporaryObjectUrl =
        URL.createObjectURL(
          response.data
        );

      imageSrc =
        temporaryObjectUrl;
    } catch {
      /*
       * Se não for possível obter como blob,
       * tentamos carregar diretamente.
       */
    }
  }

  const image =
    await new Promise(
      (
        resolve,
        reject
      ) => {
        const nextImage =
          new Image();

        if (
          !String(
            imageSrc
          ).startsWith(
            "blob:"
          ) &&
          !String(
            imageSrc
          ).startsWith(
            "data:"
          )
        ) {
          nextImage.crossOrigin =
            "anonymous";
        }

        nextImage.onload =
          () => {
            resolve(
              nextImage
            );
          };

        nextImage.onerror =
          () => {
            reject(
              new Error(
                "PROFILE_IMAGE_LOAD_FAILED"
              )
            );
          };

        nextImage.src =
          imageSrc;
      }
    );

  return {
    image,
    temporaryObjectUrl,
  };
}

async function createCroppedProfileFile({
  src,
  viewportWidth,
  viewportHeight,
  zoom,
  offsetX,
  offsetY,
  originalName,
}) {
  const {
    image,
    temporaryObjectUrl,
  } =
    await loadProfileImageForCanvas(
      src
    );

  try {
    const naturalWidth =
      image.naturalWidth;

    const naturalHeight =
      image.naturalHeight;

    const baseScale =
      Math.max(
        viewportWidth /
          naturalWidth,
        viewportHeight /
          naturalHeight
      );

    const renderScale =
      baseScale *
      zoom;

    const sourceWidth =
      viewportWidth /
      renderScale;

    const sourceHeight =
      viewportHeight /
      renderScale;

    const sourceX =
      clampProfileValue(
        naturalWidth /
          2 -
          (
            viewportWidth /
              2 +
            offsetX
          ) /
            renderScale,
        0,
        Math.max(
          0,
          naturalWidth -
            sourceWidth
        )
      );

    const sourceY =
      clampProfileValue(
        naturalHeight /
          2 -
          (
            viewportHeight /
              2 +
            offsetY
          ) /
            renderScale,
        0,
        Math.max(
          0,
          naturalHeight -
            sourceHeight
        )
      );

    const outputSize =
      800;

    const canvas =
      document.createElement(
        "canvas"
      );

    canvas.width =
      outputSize;

    canvas.height =
      outputSize;

    const context =
      canvas.getContext(
        "2d"
      );

    if (!context) {
      throw new Error(
        "PROFILE_CANVAS_UNAVAILABLE"
      );
    }

    context.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      outputSize,
      outputSize
    );

    const blob =
      await new Promise(
        (
          resolve,
          reject
        ) => {
          canvas.toBlob(
            (
              result
            ) => {
              if (
                result
              ) {
                resolve(
                  result
                );

                return;
              }

              reject(
                new Error(
                  "PROFILE_CROP_FAILED"
                )
              );
            },
            "image/jpeg",
            0.92
          );
        }
      );

    const cleanName =
      String(
        originalName ||
          "profile-photo"
      )
        .replace(
          /\.[^.]+$/,
          ""
        )
        .replace(
          /[^a-z0-9_-]+/gi,
          "-"
        );

    return new File(
      [
        blob,
      ],
      `${cleanName}-cropped.jpg`,
      {
        type:
          "image/jpeg",
      }
    );
  } finally {
    if (
      temporaryObjectUrl
    ) {
      URL.revokeObjectURL(
        temporaryObjectUrl
      );
    }
  }
}

function splitCommaValue(value) {
  if (Array.isArray(value)) {
    return value.map(String).map((x) => x.trim()).filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function boolFromSelect(value) {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return null;
}

function boolToSelect(v) {
  if (v === true) return "true";
  if (v === false) return "false";
  return "";
}

function getCountryFlagUrl(code) {
  if (!code) return "";
  return `https://flagcdn.com/w40/${code}.png`;
}

function getOptionLabel(option, t) {
  if (!option) return "";
  if (option.labelKey) return t(option.labelKey, { defaultValue: option.fallback || "" });
  return option.label || option.fallback || option.value || "";
}

function adaptFromDriverOriginal(d) {
  if (!d) return { ...emptyDriver };

  return {
    ...emptyDriver,

    fullname: d.fullname ?? d.name ?? "",
    phonenumber: d.phonenumber ?? d.phoneNumber ?? d.contact_number ?? "",
    birth_date: fmtDateForInput(d.birth_date ?? d.date_of_birth ?? ""),
    email: d.email ?? "",

    address: d.address ?? "",
    postal_code: d.postal_code ?? d.postalCode ?? "",
    city: d.city ?? d.homecity ?? "",
    country: d.country ?? "",

    tax_id: d.tax_id ?? d.driver_id ?? d.id_number ?? "",
    tax_id_expiry: fmtDateForInput(d.tax_id_expiry ?? d.DriverIDExpiry ?? ""),

    emergency_name: d.emergency_name ?? "",
    emergency_contact: d.emergency_contact ?? "",
    emergency_family: d.emergency_family ?? "",

    driving_license_number:
      d.driving_license_number ??
      d.drivingLicenseNumber ??
      d.driver_license ??
      d.driverLicense ??
      "",

    license_country: d.license_country ?? d.licenseCountry ?? "",
    license_categories: Array.isArray(d.license_categories)
      ? d.license_categories
      : d.license_categories
      ? splitCommaValue(d.license_categories)
      : [],

    tacho_card_number:
      d.tacho_card_number ??
      d.tachoCardNumber ??
      d.tachograph_card_number ??
      d.tachographCardNumber ??
      d.driver_tacho_number ??
      d.driverTachoNumber ??
      "",

    driver_tacho_number:
      d.driver_tacho_number ?? d.tacho_card_number ?? d.tachoCardNumber ?? "",

    issue_date_tacho_card: fmtDateForInput(d.issue_date_tacho_card ?? ""),
    expiry_date_tacho_card: fmtDateForInput(d.expiry_date_tacho_card ?? ""),

    home_base_start: d.home_base_start ?? "",
    home_base_ends: d.home_base_ends ?? "",

    languages_spoken: Array.isArray(d.languages_spoken)
      ? d.languages_spoken
      : d.languages_spoken
      ? splitCommaValue(d.languages_spoken)
      : [],

    work_availability: Array.isArray(d.work_availability)
      ? d.work_availability
      : d.work_availability
      ? splitCommaValue(d.work_availability)
      : [],

    lanes_preference: d.lanes_preference ?? "",
    contact_preferences: d.contact_preferences ?? "",

    distance: d.distance ?? "",
    weight: d.weight ?? "",
    temperature: d.temperature ?? "",

    take_photos: d.take_photos ?? null,
    gps_sharing: d.gps_sharing ?? null,
    urgent_jobs: !!d.urgent_jobs,
    night_weekend_driving: !!d.night_weekend_driving,
    sleep_in_truck: !!(d.sleep_in_truck ?? d.slep_in_truck),

    crane_hiab_license: !!d.crane_hiab_license,
    tanker_experience: !!d.tanker_experience,
    refrigerated_experience: !!d.refrigerated_experience,
    special_transport_experience: !!d.special_transport_experience,

    adr_hazmar_certification:
      d.adr_hazmar_certification === "N/A"
        ? "NAO"
        : d.adr_hazmar_certification ?? "NAO",

    expiry_date_adr_hazmat_certificate: fmtDateForInput(
      d.expiry_date_adr_hazmat_certificate ?? ""
    ),

    upload_photo_adr_hazmat_certificate: normalizeFileFieldArray(
      d.upload_photo_adr_hazmat_certificate
    ),

    profile_photo: normalizeFileField(d.profile_photo ?? d.profilePhoto),
    driver_id_photo: normalizeFileFieldArray(d.driver_id_photo ?? d.driverIdPhoto),
    driver_license_photo: normalizeFileFieldArray(
      d.driver_license_photo ?? d.driverLicensePhoto
    ),
    tacho_card_photo: normalizeFileFieldArray(
      d.tacho_card_photo ?? d.driver_tacho_card_photo ?? d.tachoCardPhoto
    ),

    __raw: d,
  };
}

function deriveContactNumber(prefill, cached) {
  return (
    prefill?.phonenumber ??
    prefill?.phoneNumber ??
    prefill?.contact_number ??
    cached?.phonenumber ??
    cached?.phoneNumber ??
    cached?.contact_number ??
    ""
  );
}

const EMPTY_DRIVER_FILE_INPUTS = {
  driver_id_photo: [],
  driver_license_photo: [],
  tacho_card_photo: [],
  upload_photo_adr_hazmat_certificate: [],
};

function getDriverFileSignature(
  file
) {
  if (!file) {
    return null;
  }

  return {
    name:
      file.name ||
      "",

    size:
      Number(
        file.size ||
          0
      ),

    type:
      file.type ||
      "",

    lastModified:
      Number(
        file.lastModified ||
          0
      ),
  };
}

function buildDriverEditSnapshot({
  driver,
  selectedCountry,
  phoneNumber,
  profilePhotoFile,
  fileInputs,
}) {
  const source =
    driver &&
    typeof driver ===
      "object"
      ? driver
      : {};

  const {
    __raw,
    ...editableDriver
  } =
    source;

  const normalizedFiles =
    Object.fromEntries(
      Object.entries(
        fileInputs ||
          {}
      ).map(
        (
          [
            field,
            files,
          ]
        ) => [
          field,

          (
            Array.isArray(
              files
            )
              ? files
              : []
          ).map(
            getDriverFileSignature
          ),
        ]
      )
    );

  return JSON.stringify({
    driver:
      editableDriver,

    phone: {
      countryCode:
        selectedCountry?.value ||
        "",

      number:
        String(
          phoneNumber ||
            ""
        ).replace(
          /[^\d]/g,
          ""
        ),
    },

    profilePhotoFile:
      getDriverFileSignature(
        profilePhotoFile
      ),

    fileInputs:
      normalizedFiles,
  });
}

/* ----------------------- Stable UI Components ----------------------- */

const InlineEditor = ({
  value,
  onCommit,
  onDone,
  placeholder = "",
  type = "text",
}) => {
  const [draft, setDraft] = useState(value ?? "");
  const finishedRef = useRef(false);

  const finish = () => {
    if (finishedRef.current) return;

    finishedRef.current = true;
    onCommit(draft);
    onDone();
  };

  const cancel = () => {
    if (finishedRef.current) return;

    finishedRef.current = true;
    onDone();
  };

  return (
    <input
      autoFocus
      type={type}
      value={draft}
      placeholder={placeholder}
      className="edit-driver-inlineEditor"
      onChange={(event) => {
        setDraft(event.target.value);
      }}
      onBlur={finish}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          finish();
        }

        if (event.key === "Escape") {
          event.preventDefault();
          cancel();
        }
      }}
    />
  );
};

const InfoTile = ({
  label,
  icon,
  editing,
  onEdit,
  onDone,
  display,
  children,
  className = "",
  valueClassName = "",
}) => {
  const ignoreNextClickRef =
    useRef(false);

  const handleActionMouseDown = () => {
    if (!editing) {
      return;
    }

    /*
     * O mousedown acontece antes do blur do input.
     *
     * Guardamos esta informação para impedir que o
     * clique seguinte volte imediatamente a abrir
     * o mesmo campo.
     */
    ignoreNextClickRef.current =
      true;

    /*
     * Alguns campos fecham através do blur,
     * enquanto outros, como selects e telefone,
     * podem não disparar esse blur da mesma forma.
     *
     * Este fallback garante que todos saem do
     * modo de edição.
     */
    window.setTimeout(() => {
      onDone();
    }, 0);
  };

  const handleActionClick = (
    event
  ) => {
    if (
      ignoreNextClickRef.current
    ) {
      ignoreNextClickRef.current =
        false;

      event.preventDefault();

      return;
    }

    if (editing) {
      onDone();

      return;
    }

    onEdit();
  };

  return (
    <div
      className={[
        "edit-driver-infoTile",
        editing
          ? "is-editing"
          : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="edit-driver-infoTile-icon">
        {icon}
      </span>

      <div className="edit-driver-infoTile-content">
        <span className="edit-driver-infoTile-label">
          {label}
        </span>

        {editing ? (
          <div className="edit-driver-infoTile-editor">
            {children}
          </div>
        ) : (
          <div
            className={[
              "edit-driver-infoTile-value",
              valueClassName,
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {display || "—"}
          </div>
        )}
      </div>

      <button
        type="button"
        className={[
          "edit-driver-infoTile-edit",
          editing
            ? "is-done"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onMouseDown={
          handleActionMouseDown
        }
        onClick={
          handleActionClick
        }
        aria-label={
          editing
            ? "Concluir edição"
            : `Editar ${label}`
        }
        title={
          editing
            ? "Concluir edição"
            : `Editar ${label}`
        }
      >
        {editing ? (
          <FaCheck />
        ) : (
          <FaPen />
        )}
      </button>
    </div>
  );
};

const TextInfoCard = ({
  label,
  icon,
  value,
  editing,
  onEdit,
  onDone,
  onChange,
  placeholder = "",
  type = "text",
  className = "",
}) => {
  return (
    <InfoTile
      label={label}
      icon={icon}
      editing={editing}
      onEdit={onEdit}
      onDone={onDone}
      display={value || "—"}
      className={className}
    >
      <InlineEditor
        value={value || ""}
        type={type}
        placeholder={placeholder}
        onCommit={onChange}
        onDone={onDone}
      />
    </InfoTile>
  );
};

const DateInfoCard = ({
  label,
  icon,
  value,
  editing,
  onEdit,
  onDone,
  onChange,
  className = "",
}) => {
  const inputRef = useRef(null);
  const finishedRef = useRef(false);

  const [draft, setDraft] = useState(
    value || ""
  );

  useEffect(() => {
    if (!editing) return;

    setDraft(
      value || ""
    );

    finishedRef.current = false;
  }, [
    editing,
    value,
  ]);

  const finishEditing = () => {
    if (
      finishedRef.current
    ) {
      return;
    }

    finishedRef.current = true;

    onChange(
      draft
    );

    onDone();
  };

  const cancelEditing = () => {
    if (
      finishedRef.current
    ) {
      return;
    }

    finishedRef.current = true;

    onDone();
  };

  const openCalendar = () => {
    const input =
      inputRef.current;

    if (!input) return;

    input.focus({
      preventScroll: true,
    });

    if (
      typeof input.showPicker ===
      "function"
    ) {
      input.showPicker();
      return;
    }

    input.click();
  };

  return (
    <InfoTile
      label={label}
      icon={icon}
      editing={editing}
      onEdit={onEdit}
      onDone={
        finishEditing
      }
      display={
        value || "—"
      }
      className={
        className
      }
    >
      <div className="edit-driver-inlineDate">
        <input
          ref={inputRef}
          autoFocus
          type="date"
          value={draft}
          className="edit-driver-inlineEditor edit-driver-inlineDate-input"
          onChange={(event) => {
            setDraft(
              event.target.value
            );
          }}
          onBlur={
            finishEditing
          }
          onKeyDown={(event) => {
            if (
              event.key ===
              "Enter"
            ) {
              event.preventDefault();

              finishEditing();
            }

            if (
              event.key ===
              "Escape"
            ) {
              event.preventDefault();

              cancelEditing();
            }
          }}
        />

        <button
          type="button"
          className="edit-driver-inlineDate-button"
          aria-label="Abrir calendário"
          title="Abrir calendário"
          onMouseDown={(event) => {
            /*
             * Evita que o input perca o foco
             * antes de o calendário abrir.
             */
            event.preventDefault();
          }}
          onClick={
            openCalendar
          }
        >
          <FaCalendarAlt />
        </button>
      </div>
    </InfoTile>
  );
};

const ChipInfoCard = ({
  label,
  icon,
  values,
  editing,
  onEdit,
  onDone,
  onChange,
  placeholder = "",
  formatter = (value) => value,
  className = "",
}) => {
  const list = Array.isArray(values)
    ? values.filter(Boolean)
    : [];

  return (
    <InfoTile
      label={label}
      icon={icon}
      editing={editing}
      onEdit={onEdit}
      onDone={onDone}
      className={className}
      display={
        list.length ? (
          <div className="edit-driver-chipList">
            {list.map((item, index) => (
              <span
                key={`${item}-${index}`}
                className="edit-driver-chip"
              >
                {formatter(item)}
              </span>
            ))}
          </div>
        ) : (
          "—"
        )
      }
    >
      <InlineEditor
        value={list.join(", ")}
        placeholder={placeholder}
        onCommit={(nextValue) => {
          onChange(splitCommaValue(nextValue));
        }}
        onDone={onDone}
      />
    </InfoTile>
  );
};

const CustomDropdown = ({
  value,
  options,
  onChange,
  t,
  placeholder = "",
  variant = "default",
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption =
    options.find(
      (option) =>
        String(option.value) ===
        String(value)
    ) || null;

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (!dropdownRef.current) return;

      if (
        !dropdownRef.current.contains(
          event.target
        )
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    window.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );

      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [open]);

  return (
    <div
      ref={dropdownRef}
      className={[
        "edit-driver-customSelect",
        open ? "open" : "",
        variant === "phone"
          ? "phone"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        type="button"
        className="edit-driver-customSelect-trigger"
        onClick={() => {
          setOpen(
            (previousOpen) =>
              !previousOpen
          );
        }}
      >
        {variant === "phone" &&
        selectedOption?.code ? (
          <img
            src={getCountryFlagUrl(
              selectedOption.code
            )}
            alt={
              selectedOption.label ||
              "País"
            }
            className="edit-driver-customSelect-flag"
            onError={(event) => {
              event.currentTarget.style.display =
                "none";
            }}
          />
        ) : null}

        <span className="edit-driver-customSelect-value">
          {variant === "phone"
            ? `+${
                selectedOption?.value ||
                ""
              }`
            : getOptionLabel(
                selectedOption,
                t
              ) || placeholder}
        </span>

        <span className="edit-driver-customSelect-chevron" />
      </button>

      {open && (
        <div className="edit-driver-customSelect-menu">
          {options.map((option) => {
            const active =
              String(option.value) ===
              String(value);

            return (
              <button
                key={`${option.value}-${
                  option.label ||
                  option.labelKey ||
                  ""
                }`}
                type="button"
                className={[
                  "edit-driver-customSelect-option",
                  active
                    ? "active"
                    : "",
                  String(option.value) === "true" ||
                  String(option.value) === "SIM"
                    ? "is-positive"
                    : "",
                  String(option.value) === "false" ||
                  String(option.value) === "NAO"
                    ? "is-negative"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
              >
                {variant ===
                  "phone" &&
                option.code ? (
                  <img
                    src={getCountryFlagUrl(
                      option.code
                    )}
                    alt={
                      option.label ||
                      "País"
                    }
                    className="edit-driver-customSelect-flag"
                    onError={(
                      event
                    ) => {
                      event.currentTarget.style.display =
                        "none";
                    }}
                  />
                ) : null}

                <span className="edit-driver-customSelect-optionLabel">
                  {getOptionLabel(
                    option,
                    t
                  )}
                </span>

                {variant ===
                "phone" ? (
                  <span className="edit-driver-customSelect-optionMeta">
                    +{option.value}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};


const SelectInfoCard = ({
  label,
  icon,
  value,
  options,
  t,
  editing,
  onEdit,
  onDone,
  onChange,
  className = "",
  statusStyle = false,
}) => {
  const selectedOption =
    options.find(
      (option) =>
        String(option.value) ===
        String(value)
    ) || null;

  const selectedLabel =
    getOptionLabel(
      selectedOption,
      t
    ) || "—";

  const statusClass =
    String(value) === "true" ||
    String(value) === "SIM"
      ? "is-positive"
      : String(value) === "false" ||
        String(value) === "NAO"
      ? "is-negative"
      : "is-unset";

  return (
    <InfoTile
      label={label}
      icon={icon}
      editing={editing}
      onEdit={onEdit}
      onDone={onDone}
      className={className}
      display={
        statusStyle ? (
          <span
            className={`edit-driver-valueStatus ${statusClass}`}
          >
            {selectedLabel}
          </span>
        ) : (
          selectedLabel
        )
      }
    >
      <CustomDropdown
        t={t}
        value={value}
        options={options}
        onChange={(option) => {
          onChange(option.value);
          onDone();
        }}
      />
    </InfoTile>
  );
};

const PhoneInfoCard = ({
  t,
  editing,
  onEdit,
  onDone,
  selectedCountry,
  setSelectedCountry,
  phoneNumber,
  setPhoneNumber,
}) => {
  return (
    <InfoTile
      label={t("fields.phone.label")}
      icon={<FaPhoneAlt />}
      editing={editing}
      onEdit={onEdit}
      onDone={onDone}
      display={
        phoneNumber ? (
          <span className="edit-driver-phoneDisplay">
            <img
              src={getCountryFlagUrl(
                selectedCountry?.code
              )}
              alt={
                selectedCountry?.label ||
                "País"
              }
            />

            <span>
              +
              {selectedCountry?.value ||
                ""}
            </span>

            <span>
              {phoneNumber}
            </span>
          </span>
        ) : (
          "—"
        )
      }
    >
      <div className="edit-driver-inlinePhone">
        <div className="edit-driver-inlinePhone-country">
          <CustomDropdown
            t={t}
            value={
              selectedCountry?.value ||
              "351"
            }
            options={countryOptions}
            variant="phone"
            onChange={(option) => {
              setSelectedCountry(
                option
              );
            }}
          />
        </div>

        <input
          autoFocus
          type="tel"
          value={phoneNumber || ""}
          className="edit-driver-inlineEditor"
          onChange={(event) => {
            setPhoneNumber(
              event.target.value.replace(
                /[^\d]/g,
                ""
              )
            );
          }}
          onKeyDown={(event) => {
            if (
              event.key === "Enter"
            ) {
              event.preventDefault();
              onDone();
            }

            if (
              event.key === "Escape"
            ) {
              event.preventDefault();
              onDone();
            }
          }}
        />
      </div>
    </InfoTile>
  );
};

const DriverSection = ({
  title,
  children,
  className = "",
}) => {
  return (
    <section
      className={[
        "edit-driver-dashboardSection",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="edit-driver-dashboardSection-head">
        <h2>
          {title}
        </h2>
      </div>

      <div className="edit-driver-dashboardSection-body">
        {children}
      </div>
    </section>
  );
};

const DocCard = ({
  title,
  field,
  t,
  getDocItemsWithPreview,
  openDocViewer,
  handleDocFilesChange,
  selectedFiles,
  max = 2,
}) => {
  const list =
    getDocItemsWithPreview(
      field
    );

  const shown =
    list.slice(
      0,
      max
    );

  const loadedCount =
    shown.length;

  const openAt = (
    index = 0
  ) => {
    openDocViewer(
      list,
      index
    );
  };

  const selectedText =
    selectedFiles?.length
      ? selectedFiles
          .map(
            (file) =>
              file.name
          )
          .join(", ")
      : "";

  return (
    <div className="edit-driver-docModule">
      <div
        className={[
          "edit-driver-docCard",
          loadedCount
            ? "has-files"
            : "is-empty",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="edit-driver-docCard-header">
          <div className="edit-driver-docCard-titleWrap">
            <div className="edit-driver-docCard-title">
              {title}
            </div>

            <div className="edit-driver-docCard-meta">
              <span
                className={[
                  "edit-driver-docBadge",
                  loadedCount
                    ? "ok"
                    : "missing",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {loadedCount
                  ? t(
                      "files.status.loaded"
                    )
                  : t(
                      "files.status.missing"
                    )}
              </span>

              <span>
                {loadedCount}/{max}
              </span>

              <span>
                •
              </span>

              <span>
                {t(
                  "files.hints.clickToOpen"
                )}
              </span>
            </div>
          </div>

          <div className="edit-driver-docCard-actions">
            <button
              type="button"
              disabled={!loadedCount}
              onClick={() => {
                if (
                  loadedCount
                ) {
                  openAt(0);
                }
              }}
              title={t(
                "files.actions.view"
              )}
            >
              <FaEye />
            </button>
          </div>
        </div>

        <div className="edit-driver-docCard-content">
          {loadedCount ? (
            <div className="edit-driver-docThumbs">
              {shown.map(
                (
                  item,
                  index
                ) => {
                  const url =
                    buildFileUrl(
                      item
                    );

                  const extension =
                    getExt(
                      item
                    );

                  if (
                    isImageUrl(
                      item
                    ) ||
                    String(
                      item
                    ).startsWith(
                      "blob:"
                    )
                  ) {
                    return (
                      <button
                        key={`${url}-${index}`}
                        type="button"
                        className="edit-driver-docThumbBtn"
                        onClick={() => {
                          openAt(
                            index
                          );
                        }}
                      >
                        <img
                          src={url}
                          alt={`${title} ${
                            index + 1
                          }`}
                          className="edit-driver-docThumbImg"
                          loading="lazy"
                        />

                        <span className="edit-driver-docThumbOverlay">
                          <FaEye />
                        </span>
                      </button>
                    );
                  }

                  return (
                    <button
                      key={`${url}-${index}`}
                      type="button"
                      className="edit-driver-docFileTile"
                      onClick={() => {
                        openAt(
                          index
                        );
                      }}
                    >
                      <FaFileAlt />

                      <span>
                        {(
                          extension ||
                          "FILE"
                        ).toUpperCase()}
                      </span>

                      <span className="edit-driver-docThumbOverlay">
                        <FaEye />
                      </span>
                    </button>
                  );
                }
              )}
            </div>
          ) : (
            <div className="edit-driver-docEmpty">
              <FaFileAlt />

              <div>
                <strong>
                  {t(
                    "files.empty.title"
                  )}
                </strong>

                <span>
                  {t(
                    "files.thumbs.count_other",
                    {
                      count: 0,
                      defaultValue:
                        "0 fotos",
                    }
                  )}{" "}
                  •{" "}
                  {t(
                    "files.maxShort",
                    {
                      max,
                    }
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <label className="edit-driver-uploadStrip">
        <input
          type="file"
          multiple
          accept="image/*,.pdf,.heic,.heif"
          className="edit-driver-file-input-hidden"
          onChange={(event) => {
            handleDocFilesChange(
              field,
              event.target.files
            );
          }}
        />

        <FaUpload />

        <span className="edit-driver-uploadStrip-main">
          {selectedFiles?.length
            ? t(
                "files.actions.chooseFiles",
                {
                  defaultValue:
                    "Ficheiros selecionados",
                }
              )
            : t(
                "files.hints.dragOrChoose",
                {
                  defaultValue:
                    "Arraste ficheiros ou clique para escolher",
                }
              )}
        </span>

        {selectedText ? (
          <span className="edit-driver-uploadStrip-files">
            {selectedText}
          </span>
        ) : null}
      </label>
    </div>
  );
};

/* ------------------------------ Component ------------------------------ */

function EditDriver() {
  const { t } = useTranslation("edit_driver");
  const { driverId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill ?? null;

  /*
   * A leitura é síncrona.
   *
   * Portanto, o primeiro render já recebe os dados
   * em vez de apresentar primeiro os campos vazios.
   */
  const cached =
    useMemo(
      () =>
        readDriverDetailsCache(
          driverId
        ),
      [
        driverId,
      ]
    );

  /*
   * O prefill vindo da página Drivers pode ser parcial.
   *
   * Por isso, não fazemos apenas:
   *
   * {
   *   ...cached,
   *   ...prefill
   * }
   *
   * porque valores vazios do prefill poderiam apagar
   * dados completos existentes no cache.
   */
  const hasCompleteInitialData =
    Boolean(
      prefill?.__driverDetailsComplete ===
        true ||
      cached?.__driverDetailsComplete ===
        true
    );

  /*
   * Quando a página Drivers já entregou o registo
   * completo, usamos esse objeto diretamente.
   *
   * Isto impede que uma cache antiga ou um merge de
   * preview substitua campos completos por valores
   * parciais ou vazios.
   */
  const initialSource =
    useMemo(
      () => {
        if (
          prefill?.__driverDetailsComplete ===
          true
        ) {
          return prefill;
        }

        if (
          cached?.__driverDetailsComplete ===
          true
        ) {
          return cached;
        }

        return mergeDriverPreviewData(
          cached,
          prefill
        );
      },
      [
        cached,
        prefill,
      ]
    );

  const initial =
    useMemo(
      () => {
        const base =
          adaptFromDriverOriginal(
            initialSource
          );

        const phone =
          deriveContactNumber(
            initialSource,
            cached
          );

        return {
          ...emptyDriver,
          ...base,

          phonenumber:
            phone ||
            base.phonenumber ||
            "",
        };
      },
      [
        initialSource,
        cached,
      ]
    );

  const [driver, setDriver] =
    useState(
      () =>
        initial
    );

  const hasInitialDriverData =
    useMemo(
      () =>
        Boolean(
          cached ||
          prefill
        ),
      [
        cached,
        prefill,
      ]
    );

  const phoneInit = splitPhone(initial.phonenumber || prefill?.phoneNumber);
  const [selectedCountry, setSelectedCountry] = useState(phoneInit.country);
  const [phoneNumber, setPhoneNumber] = useState(phoneInit.number);

  /*
  * Mesmo antes de o GET individual terminar,
  * guardamos os dados que vieram da lista.
  */
  useEffect(() => {
    if (
      !prefill
    ) {
      return;
    }

    primeDriverDetailsCache(
      driverId,
      prefill
    );
  }, [
    driverId,
    prefill,
  ]);

  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(
    initial.profile_photo ? buildFileUrl(initial.profile_photo) : null
  );

  const profilePhotoInputRef =
    useRef(null);

  const profileCropStageRef =
    useRef(null);

  const profileCropDragRef =
    useRef(null);

  const [profilePhotoModalOpen, setProfilePhotoModalOpen] =
    useState(false);

  const [profileEditorSource, setProfileEditorSource] =
    useState("");

  const [profileEditorFile, setProfileEditorFile] =
    useState(null);

  const [profileEditorZoom, setProfileEditorZoom] =
    useState(1);

  const [profileEditorOffset, setProfileEditorOffset] =
    useState({
      x: 0,
      y: 0,
    });

  const [profileEditorImageSize, setProfileEditorImageSize] =
    useState({
      width: 0,
      height: 0,
    });

  const [profileEditorError, setProfileEditorError] =
    useState("");

  const [isApplyingProfileCrop, setIsApplyingProfileCrop] =
    useState(false);

  const [isProfileDragging, setIsProfileDragging] =
    useState(false);

  const [fileInputs, setFileInputs] = useState({
    ...EMPTY_DRIVER_FILE_INPUTS,
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [, setSuccessMessage] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [savedEditSnapshot, setSavedEditSnapshot] =
    useState(null);

  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] =
    useState(false);

  const [pendingNavigation, setPendingNavigation] =
    useState(null);

  const historyGuardActiveRef =
    useRef(false);

  const historyGuardTokenRef =
    useRef("");

  const bypassHistoryGuardRef =
    useRef(false);

  const pendingNavigationRef =
    useRef(null);

  const saveAndLeaveRequestedRef =
    useRef(false);

  const [editingField, setEditingField] = useState(null);

  const isEditingField = (field) => {
    return editingField === field;
  };

  const startEditingField = (field) => {
    setEditingField(field);
  };

  const stopEditingField = () => {
    setEditingField(null);
  };

  const [docViewerOpen, setDocViewerOpen] = useState(false);
  const [docViewerItems, setDocViewerItems] = useState([]);
  const [docViewerIndex, setDocViewerIndex] = useState(0);

  const closeDocViewer = () => setDocViewerOpen(false);

  const openDocViewer = useCallback((items, startIndex = 0) => {
    const list = Array.isArray(items) ? items.filter(Boolean) : items ? [items] : [];
    const urls = list.map((x) => buildFileUrl(x)).filter(Boolean);

    if (!urls.length) return;

    setDocViewerItems(urls);
    setDocViewerIndex(Math.max(0, Math.min(startIndex, urls.length - 1)));
    setDocViewerOpen(true);
  }, []);

  const goPrev = () => setDocViewerIndex((i) => (i <= 0 ? i : i - 1));
  const goNext = () =>
    setDocViewerIndex((i) => (i >= docViewerItems.length - 1 ? i : i + 1));

  const updateField = (field, value) => {
    setDriver((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errorMessage) setErrorMessage("");
    setSuccessMessage("");
    if (showSuccessPopup) setShowSuccessPopup(false);
  };

  const updateCommaField = (field, value) => {
    updateField(field, splitCommaValue(value));
  };

  const viewJoin = (arr) => {
    const list = Array.isArray(arr) ? arr.filter(Boolean) : [];
    return list.length ? list.join(", ") : "";
  };

  const translateList = (arr, baseKey) => {
    const list = Array.isArray(arr) ? arr.filter(Boolean) : [];

    return list.length
      ? list.map((item) => t(`${baseKey}.${item}`, { defaultValue: item })).join(", ")
      : "";
  };

  const getBoolLabel = (value) => {
    if (value === true) return t("common.yes", { defaultValue: "Sim" });
    if (value === false) return t("common.no", { defaultValue: "Não" });
    return "—";
  };

  const currentEditSnapshot =
    useMemo(
      () =>
        buildDriverEditSnapshot({
          driver,

          selectedCountry,

          phoneNumber,

          profilePhotoFile,

          fileInputs,
        }),
      [
        driver,
        selectedCountry,
        phoneNumber,
        profilePhotoFile,
        fileInputs,
      ]
    );

  const hasUnsavedChanges =
    savedEditSnapshot !==
      null &&
    currentEditSnapshot !==
      savedEditSnapshot;

  pendingNavigationRef.current =
    pendingNavigation;

  /*
   * Se entrámos no EditDriver já com todos os dados
   * completos vindos da lista/cache, esse primeiro
   * estado passa a ser a nossa referência guardada.
   */
  useEffect(() => {
    if (
      !hasCompleteInitialData ||
      savedEditSnapshot !==
        null
    ) {
      return;
    }

    setSavedEditSnapshot(
      currentEditSnapshot
    );
  }, [
    currentEditSnapshot,
    hasCompleteInitialData,
    savedEditSnapshot,
  ]);

  /*
   * Refresh, fechar separador ou fechar browser.
   *
   * Nestes casos os browsers não permitem apresentar
   * o nosso modal personalizado, por isso usamos o
   * aviso nativo.
   */
  useEffect(() => {
    if (
      !hasUnsavedChanges
    ) {
      return undefined;
    }

    const handleBeforeUnload =
      (
        event
      ) => {
        event.preventDefault();

        event.returnValue =
          "";
      };

    window.addEventListener(
      "beforeunload",
      handleBeforeUnload
    );

    return () => {
      window.removeEventListener(
        "beforeunload",
        handleBeforeUnload
      );
    };
  }, [
    hasUnsavedChanges,
  ]);

  /*
   * Protege o botão Back do próprio browser.
   *
   * É o mesmo princípio utilizado no EditLoad.
   */
  useEffect(() => {
    if (
      hasUnsavedChanges
    ) {
      if (
        historyGuardActiveRef.current
      ) {
        return;
      }

      const guardToken =
        `edit-driver-${driverId}-${Date.now()}`;

      historyGuardTokenRef.current =
        guardToken;

      window.history.pushState(
        {
          ...(
            window.history
              .state ||
            {}
          ),

          __loadevoEditDriverGuard:
            guardToken,
        },
        "",
        window.location.href
      );

      historyGuardActiveRef.current =
        true;

      return;
    }

    if (
      !historyGuardActiveRef.current
    ) {
      return;
    }

    const currentGuardToken =
      window.history
        .state
        ?.__loadevoEditDriverGuard;

    if (
      currentGuardToken ===
      historyGuardTokenRef.current
    ) {
      bypassHistoryGuardRef.current =
        true;

      historyGuardActiveRef.current =
        false;

      window.history.back();

      window.setTimeout(
        () => {
          bypassHistoryGuardRef.current =
            false;
        },
        50
      );

      return;
    }

    historyGuardActiveRef.current =
      false;
  }, [
    hasUnsavedChanges,
    driverId,
  ]);

  useEffect(() => {
    const handlePopState =
      (
        event
      ) => {
        if (
          !hasUnsavedChanges ||
          !historyGuardActiveRef.current ||
          bypassHistoryGuardRef.current
        ) {
          return;
        }

        const targetGuardToken =
          event.state
            ?.__loadevoEditDriverGuard;

        if (
          targetGuardToken ===
          historyGuardTokenRef.current
        ) {
          return;
        }

        const shouldLeave =
          window.confirm(
            t(
              "unsavedChanges.browserWarning",
              {
                defaultValue:
                  "Tem alterações por guardar. Se sair agora, poderá perdê-las.",
              }
            )
          );

        if (
          shouldLeave
        ) {
          bypassHistoryGuardRef.current =
            true;

          historyGuardActiveRef.current =
            false;

          window.history.back();

          window.setTimeout(
            () => {
              bypassHistoryGuardRef.current =
                false;
            },
            50
          );

          return;
        }

        bypassHistoryGuardRef.current =
          true;

        window.history.forward();

        window.setTimeout(
          () => {
            bypassHistoryGuardRef.current =
              false;
          },
          50
        );
      };

    window.addEventListener(
      "popstate",
      handlePopState
    );

    return () => {
      window.removeEventListener(
        "popstate",
        handlePopState
      );
    };
  }, [
    hasUnsavedChanges,
    t,
  ]);

  /*
   * Interceta links da Sidebar/Header enquanto
   * existirem alterações por guardar.
   */
  useEffect(() => {
    if (
      !hasUnsavedChanges
    ) {
      return undefined;
    }

    const handleDocumentClick =
      (
        event
      ) => {
        if (
          event.defaultPrevented ||
          event.button !==
            0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        const target =
          event.target;

        const anchor =
          target
            ?.closest
            ? target.closest(
                "a[href]"
              )
            : null;

        if (!anchor) {
          return;
        }

        if (
          anchor.target ===
            "_blank" ||
          anchor.hasAttribute(
            "download"
          )
        ) {
          return;
        }

        const rawHref =
          anchor.getAttribute(
            "href"
          );

        if (
          !rawHref ||
          rawHref.startsWith(
            "#"
          )
        ) {
          return;
        }

        let targetUrl;

        try {
          targetUrl =
            new URL(
              anchor.href,
              window.location.href
            );
        } catch {
          return;
        }

        const currentUrl =
          new URL(
            window.location.href
          );

        if (
          targetUrl.href ===
          currentUrl.href
        ) {
          return;
        }

        event.preventDefault();

        event.stopPropagation();

        const nextNavigation = {
          type:
            targetUrl.origin ===
            currentUrl.origin
              ? "internal"
              : "external",

          href:
            targetUrl.href,
        };

        pendingNavigationRef.current =
          nextNavigation;

        setPendingNavigation(
          nextNavigation
        );

        setUnsavedChangesModalOpen(
          true
        );
      };

    document.addEventListener(
      "click",
      handleDocumentClick,
      true
    );

    return () => {
      document.removeEventListener(
        "click",
        handleDocumentClick,
        true
      );
    };
  }, [
    hasUnsavedChanges,
  ]);

  const buildPayload = () => {
    const fullPhone = phoneNumber
      ? `+${selectedCountry?.value || ""}${String(phoneNumber).replace(/[^\d]/g, "")}`
      : "";

    return {
      ...driver,

      phonenumber: fullPhone,

      contact_number: fullPhone,
      phoneNumber: fullPhone,
      postalCode: driver.postal_code,

      tacho_card_number: driver.tacho_card_number || driver.driver_tacho_number || "",
      driver_tacho_number: driver.driver_tacho_number || driver.tacho_card_number || "",

      license_categories: Array.isArray(driver.license_categories)
        ? driver.license_categories
        : splitCommaValue(driver.license_categories),

      languages_spoken: Array.isArray(driver.languages_spoken)
        ? driver.languages_spoken
        : splitCommaValue(driver.languages_spoken),

      work_availability: Array.isArray(driver.work_availability)
        ? driver.work_availability
        : splitCommaValue(driver.work_availability),
    };
  };

  const appendPayloadToFormData = (formData, payload) => {
    Object.entries(payload).forEach(([key, value]) => {
      if (key === "__raw") return;

      if (
        key === "profile_photo" ||
        key === "driver_id_photo" ||
        key === "driver_license_photo" ||
        key === "tacho_card_photo" ||
        key === "upload_photo_adr_hazmat_certificate"
      ) {
        formData.append(
          `${key}_existing`,
          JSON.stringify(value || (Array.isArray(value) ? [] : null))
        );
        return;
      }

      if (Array.isArray(value)) {
        formData.append(key, JSON.stringify(value));
        return;
      }

      if (value === null || value === undefined) {
        formData.append(key, "");
        return;
      }

      formData.append(key, value);
    });

    if (profilePhotoFile) {
      formData.append("profile_photo", profilePhotoFile);
    }

    Object.entries(fileInputs).forEach(([field, files]) => {
      (files || []).slice(0, 2).forEach((file) => {
        formData.append(field, file);
      });
    });
  };

  const hasNewUploadFiles = () => {
    /*
     * A foto de perfil NÃO é enviada pelo PUT geral.
     *
     * O backend já possui:
     *
     * POST /driveroriginal/:id/profile-photo
     *
     * exclusivamente para esse upload.
     */
    return Object.values(fileInputs).some((files) => {
      return Array.isArray(files) && files.length > 0;
    });
  };

  const saveProfilePhoto = async () => {
    if (!profilePhotoFile) {
      return null;
    }

    const photoFormData =
      new FormData();

    /*
     * O backend usa:
     *
     * driverProfileUpload.single("file")
     *
     * portanto o nome deste campo tem de ser
     * exatamente "file".
     */
    photoFormData.append(
      "file",
      profilePhotoFile,
      profilePhotoFile.name ||
        "profile-photo.jpg"
    );

    console.log(
      "[EditDriver] uploading profile photo",
      {
        driverId,
        name:
          profilePhotoFile.name,
        type:
          profilePhotoFile.type,
        size:
          profilePhotoFile.size,
        hasFile:
          photoFormData.has(
            "file"
          ),
      }
    );

    const response =
      await axios.post(
        `/driveroriginal/${driverId}/profile-photo`,
        photoFormData,
        {
          headers: {
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

    return (
      response?.data ||
      null
    );
  };

  const saveDriver = async (payload, formData) => {
    const hasUploads = hasNewUploadFiles();

    if (!hasUploads) {
      try {
        return await axios.put(`/driveroriginal/${driverId}`, payload);
      } catch (err1) {
        return await axios.put(`/drivers/${driverId}`, payload);
      }
    }

    try {
      return await axios.put(`/driveroriginal/${driverId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (err1) {
      try {
        return await axios.put(`/drivers/${driverId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (err2) {
        try {
          return await axios.put(`/driveroriginal/${driverId}`, payload);
        } catch {
          return await axios.put(`/drivers/${driverId}`, payload);
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const payload = buildPayload();
      const formData = new FormData();

      appendPayloadToFormData(formData, payload);

      const response =
        await saveDriver(
          payload,
          formData
        );

      /*
       * A foto de perfil utiliza a rota própria
       * de upload do backend.
       *
       * Fazemos isto depois de guardar os restantes
       * dados do motorista.
       */
      const profileUploadResponse =
        await saveProfilePhoto();

      const profilePhotoStablePath =
        profileUploadResponse?.stablePath ||
        profileUploadResponse?.driver?.profile_photo ||
        null;

      const savedDriverSource = {
        ...(
          response?.data ||
          payload
        ),

        ...(
          profileUploadResponse?.driver ||
          {}
        ),

        ...(
          profilePhotoStablePath
            ? {
                profile_photo:
                  profilePhotoStablePath,
              }
            : {}
        ),
      };

      const updated =
        adaptFromDriverOriginal(
          savedDriverSource
        );

      const nextDriver = {
        ...driver,
        ...updated,
      };

      setDriver(
        nextDriver
      );

      let nextSelectedCountry =
        selectedCountry;

      let nextPhoneNumber =
        phoneNumber;

      if (
        updated?.phonenumber
      ) {
        const phone =
          splitPhone(
            updated.phonenumber
          );

        nextSelectedCountry =
          phone.country;

        nextPhoneNumber =
          phone.number;

        setSelectedCountry(
          phone.country
        );

        setPhoneNumber(
          phone.number
        );
      }

      /*
       * Para o preview imediato preferimos a URL
       * fresca devolvida pelo upload.
       *
       * Para cache/persistência continuamos a guardar
       * o stablePath.
       */
      if (
        profileUploadResponse?.url
      ) {
        setProfilePhotoPreview(
          profileUploadResponse.url
        );
      } else if (
        updated?.profile_photo
      ) {
        setProfilePhotoPreview(
          buildFileUrl(
            updated.profile_photo
          )
        );
      }

      const clearedFileInputs = {
        ...EMPTY_DRIVER_FILE_INPUTS,
      };

      setProfilePhotoFile(
        null
      );

      setFileInputs(
        clearedFileInputs
      );

      /*
       * A partir deste momento esta versão passa
       * a ser a referência guardada.
       */
      setSavedEditSnapshot(
        buildDriverEditSnapshot({
          driver:
            nextDriver,

          selectedCountry:
            nextSelectedCountry,

          phoneNumber:
            nextPhoneNumber,

          profilePhotoFile:
            null,

          fileInputs:
            clearedFileInputs,
        })
      );

      writeDriverDetailsCache(
        driverId,
        {
          ...updated,

          __driverDetailsComplete:
            true,
        }
      );

      const updatedFields = response?.data?.fields;

      if (Array.isArray(updatedFields) && updatedFields.length === 0) {
        saveAndLeaveRequestedRef.current =
          false;

        setErrorMessage(
          t("errors.noChangesSaved", {
            defaultValue:
              "Nenhuma alteração foi guardada. Verifica se a rota está a receber os campos corretamente.",
          })
        );
        return;
      }

      /*
       * Se o save nasceu da opção "Guardar e sair",
       * seguimos para o destino que o utilizador
       * tinha escolhido.
       */
      if (
        saveAndLeaveRequestedRef.current
      ) {
        const navigation =
          pendingNavigationRef.current;

        saveAndLeaveRequestedRef.current =
          false;

        pendingNavigationRef.current =
          null;

        setPendingNavigation(
          null
        );

        setUnsavedChangesModalOpen(
          false
        );

        setShowSuccessPopup(
          false
        );

        performPendingNavigation(
          navigation
        );

        return;
      }

      setShowSuccessPopup(true);

      setTimeout(() => {
        navigate("/drivers");
      }, 1200);
    } catch (err) {
      saveAndLeaveRequestedRef.current =
        false;

      console.error("Error saving driver:", err);

      setErrorMessage(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          t("errors.saveFailed", {
            defaultValue: "Falha ao guardar as alterações. Tente novamente.",
          })
      );
    } finally {
      setIsSaving(false);
    }
  };

  const clampProfileEditorOffset =
    useCallback(
      (
        nextOffset,
        zoomValue =
          profileEditorZoom
      ) => {
        const stage =
          profileCropStageRef.current;

        if (
          !stage ||
          !profileEditorImageSize.width ||
          !profileEditorImageSize.height
        ) {
          return nextOffset;
        }

        const rect =
          stage.getBoundingClientRect();

        const baseScale =
          Math.max(
            rect.width /
              profileEditorImageSize.width,
            rect.height /
              profileEditorImageSize.height
          );

        const renderScale =
          baseScale *
          zoomValue;

        const renderedWidth =
          profileEditorImageSize.width *
          renderScale;

        const renderedHeight =
          profileEditorImageSize.height *
          renderScale;

        const maxOffsetX =
          Math.max(
            0,
            (
              renderedWidth -
              rect.width
            ) /
              2
          );

        const maxOffsetY =
          Math.max(
            0,
            (
              renderedHeight -
              rect.height
            ) /
              2
          );

        return {
          x:
            clampProfileValue(
              nextOffset.x,
              -maxOffsetX,
              maxOffsetX
            ),

          y:
            clampProfileValue(
              nextOffset.y,
              -maxOffsetY,
              maxOffsetY
            ),
        };
      },
      [
        profileEditorImageSize,
        profileEditorZoom,
      ]
    );

  const openProfilePhotoModal =
    () => {
      setProfileEditorSource(
        profilePhotoPreview ||
          ""
      );

      setProfileEditorFile(
        null
      );

      setProfileEditorZoom(
        1
      );

      setProfileEditorOffset({
        x: 0,
        y: 0,
      });

      setProfileEditorImageSize({
        width: 0,
        height: 0,
      });

      setProfileEditorError(
        ""
      );

      setProfilePhotoModalOpen(
        true
      );
    };

  const closeProfilePhotoModal =
    () => {
      if (
        String(
          profileEditorSource
        ).startsWith(
          "blob:"
        ) &&
        profileEditorSource !==
          profilePhotoPreview
      ) {
        URL.revokeObjectURL(
          profileEditorSource
        );
      }

      profileCropDragRef.current =
        null;

      setIsProfileDragging(
        false
      );

      setProfilePhotoModalOpen(
        false
      );

      setProfileEditorError(
        ""
      );
    };

  const handleProfilePhotoChange =
    (
      event
    ) => {
      const file =
        event.target.files?.[
          0
        ] ||
        null;

      /*
       * Permite selecionar novamente o mesmo ficheiro.
       */
      event.target.value =
        "";

      if (!file) {
        return;
      }

      if (
        String(
          profileEditorSource
        ).startsWith(
          "blob:"
        ) &&
        profileEditorSource !==
          profilePhotoPreview
      ) {
        URL.revokeObjectURL(
          profileEditorSource
        );
      }

      const nextSource =
        URL.createObjectURL(
          file
        );

      setProfileEditorFile(
        file
      );

      setProfileEditorSource(
        nextSource
      );

      setProfileEditorZoom(
        1
      );

      setProfileEditorOffset({
        x: 0,
        y: 0,
      });

      setProfileEditorImageSize({
        width: 0,
        height: 0,
      });

      setProfileEditorError(
        ""
      );

      setProfilePhotoModalOpen(
        true
      );
    };

  const handleProfileEditorPointerDown =
    (
      event
    ) => {
      if (
        !profileEditorSource
      ) {
        return;
      }

      event.currentTarget.setPointerCapture?.(
        event.pointerId
      );

      profileCropDragRef.current =
        {
          startX:
            event.clientX,

          startY:
            event.clientY,

          offsetX:
            profileEditorOffset.x,

          offsetY:
            profileEditorOffset.y,
        };

      setIsProfileDragging(
        true
      );
    };

  const handleProfileEditorPointerMove =
    (
      event
    ) => {
      const drag =
        profileCropDragRef.current;

      if (!drag) {
        return;
      }

      event.preventDefault();

      const nextOffset =
        {
          x:
            drag.offsetX +
            (
              event.clientX -
              drag.startX
            ),

          y:
            drag.offsetY +
            (
              event.clientY -
              drag.startY
            ),
        };

      setProfileEditorOffset(
        clampProfileEditorOffset(
          nextOffset
        )
      );
    };

  const stopProfileEditorDrag =
    (
      event
    ) => {
      event.currentTarget.releasePointerCapture?.(
        event.pointerId
      );

      profileCropDragRef.current =
        null;

      setIsProfileDragging(
        false
      );
    };

  const resetProfileEditor =
    () => {
      setProfileEditorZoom(
        1
      );

      setProfileEditorOffset({
        x: 0,
        y: 0,
      });

      setProfileEditorError(
        ""
      );
    };

  const handleApplyProfilePhoto =
    async () => {
      const stage =
        profileCropStageRef.current;

      if (
        !stage ||
        !profileEditorSource
      ) {
        return;
      }

      const rect =
        stage.getBoundingClientRect();

      setIsApplyingProfileCrop(
        true
      );

      setProfileEditorError(
        ""
      );

      try {
        const croppedFile =
          await createCroppedProfileFile({
            src:
              profileEditorSource,

            viewportWidth:
              rect.width,

            viewportHeight:
              rect.height,

            zoom:
              profileEditorZoom,

            offsetX:
              profileEditorOffset.x,

            offsetY:
              profileEditorOffset.y,

            originalName:
              profileEditorFile?.name ||
              profilePhotoFile?.name ||
              "profile-photo.jpg",
          });

        const nextPreview =
          URL.createObjectURL(
            croppedFile
          );

        if (
          String(
            profilePhotoPreview
          ).startsWith(
            "blob:"
          )
        ) {
          URL.revokeObjectURL(
            profilePhotoPreview
          );
        }

        setProfilePhotoFile(
          croppedFile
        );

        setProfilePhotoPreview(
          nextPreview
        );

        closeProfilePhotoModal();
      } catch (
        error
      ) {
        console.error(
          "Error cropping profile photo:",
          error
        );

        setProfileEditorError(
          t(
            "profile.cropError",
            {
              defaultValue:
                "Não foi possível ajustar esta imagem. Tente escolher outra foto.",
            }
          )
        );
      } finally {
        setIsApplyingProfileCrop(
          false
        );
      }
    };

  useEffect(() => {
    if (
      !profilePhotoModalOpen
    ) {
      return undefined;
    }

    const previousOverflow =
      document.body.style
        .overflow;

    document.body.style.overflow =
      "hidden";

    const handleKeyDown =
      (
        event
      ) => {
        if (
          event.key ===
          "Escape"
        ) {
          closeProfilePhotoModal();
        }
      };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [
    profilePhotoModalOpen,
  ]);

  const handleDocFilesChange = (field, files) => {
    const nextFiles = Array.from(files || []).slice(0, 2);

    setFileInputs((prev) => ({
      ...prev,
      [field]: nextFiles,
    }));
  };

  const getDocItemsWithPreview = (field) => {
    const existing = Array.isArray(driver[field])
      ? driver[field]
      : driver[field]
      ? [driver[field]]
      : [];

    const newFiles = (fileInputs[field] || []).map((file) => URL.createObjectURL(file));

    return [...newFiles, ...existing].slice(0, 2);
  };

  const getDocCount = (field) => {
    const existing = Array.isArray(driver[field])
      ? driver[field].filter(Boolean).length
      : driver[field]
      ? 1
      : 0;

    const fresh = Array.isArray(fileInputs[field]) ? fileInputs[field].length : 0;
    return Math.min(2, existing + fresh);
  };

  useEffect(() => {
    if (!docViewerOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") closeDocViewer();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };

    window.addEventListener("keydown", onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docViewerOpen, docViewerIndex, docViewerItems.length]);

  useEffect(() => {
    if (!docViewerOpen) return;

    const prev = docViewerItems[docViewerIndex - 1];
    const next = docViewerItems[docViewerIndex + 1];

    preloadImage(prev);
    preloadImage(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docViewerOpen, docViewerIndex, docViewerItems]);

  useEffect(() => {
    /*
     * O summary da página Drivers já entregou todos
     * os campos necessários ao formulário.
     *
     * Não fazemos outro GET nem outro setDriver(),
     * porque essa segunda atualização era o que fazia
     * os valores piscarem.
     */
    if (
      hasCompleteInitialData
    ) {
      return undefined;
    }

    let alive = true;

    const fetchDriver = async () => {
      try {
        let d;

        try {
          const r1 = await axios.get(`/driveroriginal/${driverId}`);
          d = r1.data || {};
        } catch {
          const r2 = await axios.get(`/drivers/${driverId}`);
          d = r2.data || {};
        }

        const hydrated =
          adaptFromDriverOriginal(
            d
          );

        if (
          !alive
        ) {
          return;
        }

        /*
         * O GET individual é a fonte autoritativa.
         *
         * O marcador fica apenas no cache e permite
         * que a página Drivers saiba que este registo
         * já contém todos os detalhes.
         */
        writeDriverDetailsCache(
          driverId,
          {
            ...hydrated,

            __driverDetailsComplete:
              true,
          }
        );

        setDriver(
          (
            previousDriver
          ) => ({
            ...previousDriver,
            ...hydrated,
          })
        );

        const hydratedPhone =
          splitPhone(
            hydrated?.phonenumber ||
              ""
          );

        if (hydrated?.phonenumber && String(hydrated.phonenumber).trim()) {
          setSelectedCountry(
            hydratedPhone.country
          );

          setPhoneNumber(
            hydratedPhone.number
          );
        }

        if (hydrated?.profile_photo) {
          setProfilePhotoPreview(
            buildFileUrl(
              hydrated.profile_photo
            )
          );
        }

        /*
         * O GET terminou.
         *
         * Este é agora o estado oficialmente guardado,
         * portanto não deve aparecer qualquer aviso
         * simplesmente por termos carregado os dados.
         */
        setSavedEditSnapshot(
          buildDriverEditSnapshot({
            driver:
              hydrated,

            selectedCountry:
              hydratedPhone.country,

            phoneNumber:
              hydratedPhone.number,

            profilePhotoFile:
              null,

            fileInputs:
              EMPTY_DRIVER_FILE_INPUTS,
          })
        );

      } catch (e) {
        console.error(
          "Error fetching driver:",
          e
        );

        /*
         * Se já temos informação em cache, mantemos a
         * página funcional e não substituímos a experiência
         * por um erro visual.
         *
         * O erro só aparece quando não existe absolutamente
         * nenhuma informação para apresentar.
         */
        if (
          !hasInitialDriverData
        ) {
          setErrorMessage(
            t(
              "errors.loadFailed"
            )
          );
        }
      }
    };

    fetchDriver();

    return () => {
      alive = false;
    };
  }, [
    driverId,
    t,
    hasInitialDriverData,
    hasCompleteInitialData,
  ]);

  const summaryDocs = [
    {
      label: t("files.kind.identification"),
      field: "driver_id_photo",
    },
    {
      label: t("files.kind.license"),
      field: "driver_license_photo",
    },
    {
      label: t("files.kind.tacho"),
      field: "tacho_card_photo",
    },
    {
      label: t("files.kind.adr"),
      field: "upload_photo_adr_hazmat_certificate",
    },
  ];

  const requestNavigation =
    (
      to,
      options = {}
    ) => {
      if (
        !hasUnsavedChanges
      ) {
        navigate(
          to,
          options
        );

        return;
      }

      const nextNavigation = {
        type:
          "navigate",

        to,

        options,
      };

      pendingNavigationRef.current =
        nextNavigation;

      setPendingNavigation(
        nextNavigation
      );

      setUnsavedChangesModalOpen(
        true
      );
    };

  const handleContinueEditing =
    () => {
      pendingNavigationRef.current =
        null;

      setPendingNavigation(
        null
      );

      setUnsavedChangesModalOpen(
        false
      );
    };

  const performPendingNavigation =
    (
      navigation
    ) => {
      if (
        !navigation
      ) {
        return;
      }

      bypassHistoryGuardRef.current =
        true;

      historyGuardActiveRef.current =
        false;

      if (
        navigation.type ===
        "navigate"
      ) {
        navigate(
          navigation.to,
          navigation.options ||
            {}
        );

        return;
      }

      if (
        navigation.type ===
        "internal"
      ) {
        const targetUrl =
          new URL(
            navigation.href
          );

        navigate(
          `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
        );

        return;
      }

      if (
        navigation.type ===
        "external"
      ) {
        window.location.assign(
          navigation.href
        );
      }
    };

  const handleLeaveWithoutSaving =
    () => {
      const navigation =
        pendingNavigationRef.current;

      pendingNavigationRef.current =
        null;

      setPendingNavigation(
        null
      );

      setUnsavedChangesModalOpen(
        false
      );

      performPendingNavigation(
        navigation
      );
    };

  const handleSaveAndLeave =
    () => {
      saveAndLeaveRequestedRef.current =
        true;

      setUnsavedChangesModalOpen(
        false
      );

      const form =
        document.getElementById(
          "edit-driver-form"
        );

      form?.requestSubmit();
    };

  return (
    <div className="edit-driver-page">
      <div className="edit-driver-container">
        <header className="edit-driver-topbar">
          <button
            type="button"
            className="edit-driver-topbar-back"
            onClick={() => {
              requestNavigation(
                "/drivers"
              );
            }}
          >
            <FaArrowLeft />

            <span>
              {t(
                "navigation.back"
              )}
            </span>
          </button>

          <div className="edit-driver-topbar-titleWrap">
            <h1 className="edit-driver-title">
              {t(
                "page.title"
              )}
            </h1>

            <p className="edit-driver-subtitle">
              {t(
                "page.subtitle",
                {
                  defaultValue:
                    "Atualize as informações e preferências do motorista.",
                }
              )}
            </p>
          </div>
        </header>

        {errorMessage && (
          <p className="edit-driver-msg-error">
            {errorMessage}
          </p>
        )}

        {showSuccessPopup && (
          <div className="edit-driver-successPopup-overlay">
            <div className="edit-driver-successPopup">
              <div className="edit-driver-successPopup-icon">
                ✓
              </div>

              <h3>
                {t(
                  "success.savedTitle",
                  {
                    defaultValue:
                      "Motorista atualizado",
                  }
                )}
              </h3>

              <p>
                {t(
                  "success.saved",
                  {
                    defaultValue:
                      "Motorista atualizado com sucesso.",
                  }
                )}
              </p>

              <button
                type="button"
                className="edit-driver-successPopup-btn"
                onClick={() => {
                  navigate(
                    "/drivers"
                  );
                }}
              >
                {t(
                  "actions.ok",
                  {
                    defaultValue:
                      "OK",
                  }
                )}
              </button>
            </div>
          </div>
        )}

        <form
          id="edit-driver-form"
          className="edit-driver-layout"
          onSubmit={
            handleSubmit
          }
          encType="multipart/form-data"
        >
          <aside
            className="edit-driver-profileSidebar"
            id="edit-driver-profile-sidebar"
          >
            <div className="edit-driver-profileCard">
              <button
                type="button"
                className="edit-driver-profilePhotoWrap"
                title={
                  profilePhotoPreview
                    ? t(
                        "profile.editTitle"
                      )
                    : t(
                        "profile.choosePhoto"
                      )
                }
                onClick={
                  openProfilePhotoModal
                }
              >
                {profilePhotoPreview ? (
                  <img
                    src={
                      profilePhotoPreview
                    }
                    alt={t(
                      "profile.alt"
                    )}
                    className="edit-driver-profile-photo-img"
                  />
                ) : (
                  <span className="edit-driver-profile-photo-placeholder">
                    <FaUpload />

                    <span>
                      {t(
                        "profile.choosePhoto"
                      )}
                    </span>
                  </span>
                )}

                <span className="edit-driver-profilePhotoEdit">
                  <FaPen />
                </span>
              </button>

              <input
                ref={
                  profilePhotoInputRef
                }
                type="file"
                accept="image/*,.heic,.heif"
                className="edit-driver-file-input-hidden"
                onChange={
                  handleProfilePhotoChange
                }
              />

              <div className="edit-driver-profileIdentity">
                <h3>
                  {driver.fullname ||
                    "—"}
                </h3>

                <span className="edit-driver-statusBadge">
                  <span className="edit-driver-statusDot" />

                  {t(
                    "profile.statusActive",
                    {
                      defaultValue:
                        "Ativo",
                    }
                  )}
                </span>
              </div>

              <div className="edit-driver-sidebarBlock">
                <div className="edit-driver-sidebarBlockTitle">
                  {t(
                    "sidebar.contact",
                    {
                      defaultValue:
                        "Contacto",
                    }
                  )}
                </div>

                <div className="edit-driver-sidebarInfoRow">
                  <FaPhoneAlt />

                  <span>
                    {phoneNumber
                      ? `+${
                          selectedCountry?.value ||
                          ""
                        } ${phoneNumber}`
                      : "—"}
                  </span>
                </div>

                <div className="edit-driver-sidebarInfoRow">
                  <FaEnvelope />

                  <span>
                    {driver.email ||
                      "—"}
                  </span>
                </div>
              </div>

              <div className="edit-driver-sidebarBlock">
                <div className="edit-driver-sidebarBlockTitle">
                  {t(
                    "sidebar.location",
                    {
                      defaultValue:
                        "Localização",
                    }
                  )}
                </div>

                <div className="edit-driver-sidebarInfoRow align-start">
                  <FaMapMarkerAlt />

                  <span>
                    {driver.address ||
                      "—"}

                    <br />

                    {[
                      driver.postal_code,
                      driver.city,
                      driver.country,
                    ]
                      .filter(
                        Boolean
                      )
                      .join(
                        ", "
                      ) || "—"}
                  </span>
                </div>
              </div>

              <div className="edit-driver-sidebarBlock">
                <div className="edit-driver-sidebarBlockTitle">
                  {t(
                    "sections.uploadDocuments"
                  )}
                </div>

                <div className="edit-driver-docSummaryList">
                  {summaryDocs.map(
                    (item) => {
                      const loaded =
                        getDocCount(
                          item.field
                        ) > 0;

                      return (
                        <div
                          key={
                            item.field
                          }
                          className="edit-driver-docSummaryRow"
                        >
                          <span className="edit-driver-docSummaryLabel">
                            <FaFileAlt />

                            {item.field ===
                            "driver_license_photo"
                              ? t(
                                  "sidebar.driverLicense",
                                  {
                                    defaultValue:
                                      "Carta de condução",
                                  }
                                )
                              : item.label}
                          </span>

                          <span
                            className={[
                              "edit-driver-miniStatus",
                              loaded
                                ? "ok"
                                : "missing",
                            ]
                              .filter(
                                Boolean
                              )
                              .join(
                                " "
                              )}
                          >
                            {loaded
                              ? t(
                                  "files.status.loaded"
                                )
                              : t(
                                  "files.status.missing"
                                )}
                          </span>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>

              <div className="edit-driver-sidebarBlock">
                <div className="edit-driver-sidebarBlockTitle">
                  {t(
                    "sidebar.quickSummary",
                    {
                      defaultValue:
                        "Resumo rápido",
                    }
                  )}
                </div>

                <div className="edit-driver-quickSummary">
                  <div className="edit-driver-quickSummaryRow">
                    <span>
                      {t(
                        "fields.licenseCategories.label"
                      )}
                    </span>

                    <strong>
                      {viewJoin(
                        driver.license_categories
                      ) || "—"}
                    </strong>
                  </div>

                  <div className="edit-driver-quickSummaryRow">
                    <span>
                      {t(
                        "fields.adr.label"
                      )}
                    </span>

                    <strong>
                      {driver.adr_hazmar_certification ===
                      "SIM"
                        ? t(
                            "common.yes",
                            {
                              defaultValue:
                                "Sim",
                            }
                          )
                        : t(
                            "common.no",
                            {
                              defaultValue:
                                "Não",
                            }
                          )}
                    </strong>
                  </div>

                  <div className="edit-driver-quickSummaryRow">
                    <span>
                      {t(
                        "fields.workAvailability.label"
                      )}
                    </span>

                    <strong>
                      {translateList(
                        driver.work_availability,
                        "fields.workAvailabilityOptions"
                      ) || "—"}
                    </strong>
                  </div>

                  <div className="edit-driver-quickSummaryRow">
                    <span>
                      {t(
                        "fields.contactPreferences.label"
                      )}
                    </span>

                    <strong>
                      {driver.contact_preferences ||
                        "—"}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <main className="edit-driver-main">
            <div className="edit-driver-dashboardCard">
              <DriverSection
                title={t(
                  "sections.basicInfo"
                )}
              >
                <div className="edit-driver-infoGrid edit-driver-infoGrid-2">
                  <TextInfoCard
                    label={t(
                      "fields.fullname.label"
                    )}
                    icon={<FaUser />}
                    value={
                      driver.fullname
                    }
                    editing={isEditingField(
                      "fullname"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "fullname"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "fullname",
                        value
                      );
                    }}
                  />

                  <DateInfoCard
                    label={t(
                      "fields.birthDate.label"
                    )}
                    icon={
                      <FaCalendarAlt />
                    }
                    value={
                      driver.birth_date
                    }
                    editing={isEditingField(
                      "birth_date"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "birth_date"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "birth_date",
                        value
                      );
                    }}
                  />

                  <PhoneInfoCard
                    t={t}
                    editing={isEditingField(
                      "phonenumber"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "phonenumber"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    selectedCountry={
                      selectedCountry
                    }
                    setSelectedCountry={
                      setSelectedCountry
                    }
                    phoneNumber={
                      phoneNumber
                    }
                    setPhoneNumber={
                      setPhoneNumber
                    }
                  />

                  <TextInfoCard
                    label={t(
                      "fields.email.label"
                    )}
                    icon={
                      <FaEnvelope />
                    }
                    value={
                      driver.email
                    }
                    type="email"
                    editing={isEditingField(
                      "email"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "email"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "email",
                        value
                      );
                    }}
                  />

                  <TextInfoCard
                    label={t(
                      "fields.address.label"
                    )}
                    icon={<FaHome />}
                    value={
                      driver.address
                    }
                    editing={isEditingField(
                      "address"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "address"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "address",
                        value
                      );
                    }}
                  />

                  <TextInfoCard
                    label={t(
                      "fields.postalCode.label"
                    )}
                    icon={
                      <FaMapMarkerAlt />
                    }
                    value={
                      driver.postal_code
                    }
                    editing={isEditingField(
                      "postal_code"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "postal_code"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "postal_code",
                        value
                      );
                    }}
                  />

                  <TextInfoCard
                    label={t(
                      "fields.city.label"
                    )}
                    icon={
                      <FaMapMarkerAlt />
                    }
                    value={
                      driver.city
                    }
                    editing={isEditingField(
                      "city"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "city"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "city",
                        value
                      );
                    }}
                  />

                  <TextInfoCard
                    label={t(
                      "fields.countryIso2.label"
                    )}
                    icon={
                      <FaGlobe />
                    }
                    value={
                      driver.country?.toUpperCase() ===
                      "PT"
                        ? "Portugal (PT)"
                        : driver.country
                    }
                    editing={isEditingField(
                      "country"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "country"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "country",
                        value
                      );
                    }}
                  />
                </div>
              </DriverSection>

              <DriverSection
                title={t(
                  "sections.identification"
                )}
              >
                <div className="edit-driver-infoGrid edit-driver-infoGrid-2">
                  <TextInfoCard
                    label={t(
                      "fields.taxId.label"
                    )}
                    icon={<FaIdCard />}
                    value={
                      driver.tax_id
                    }
                    editing={isEditingField(
                      "tax_id"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "tax_id"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "tax_id",
                        value
                      );
                    }}
                  />

                  <DateInfoCard
                    label={t(
                      "fields.taxIdExpiry.label"
                    )}
                    icon={
                      <FaCalendarAlt />
                    }
                    value={
                      driver.tax_id_expiry
                    }
                    editing={isEditingField(
                      "tax_id_expiry"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "tax_id_expiry"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "tax_id_expiry",
                        value
                      );
                    }}
                  />
                </div>

                <div className="edit-driver-infoGrid edit-driver-infoGrid-3">
                  <TextInfoCard
                    label={t(
                      "fields.drivingLicenseNumber.label"
                    )}
                    icon={<FaIdCard />}
                    value={
                      driver.driving_license_number
                    }
                    editing={isEditingField(
                      "driving_license_number"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "driving_license_number"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "driving_license_number",
                        value
                      );
                    }}
                  />

                  <TextInfoCard
                    label={t(
                      "fields.licenseCountry.label"
                    )}
                    icon={
                      <FaGlobe />
                    }
                    value={
                      driver.license_country
                    }
                    editing={isEditingField(
                      "license_country"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "license_country"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "license_country",
                        value
                      );
                    }}
                  />

                  <ChipInfoCard
                    label={t(
                      "fields.licenseCategories.label"
                    )}
                    icon={<FaIdCard />}
                    values={
                      driver.license_categories
                    }
                    editing={isEditingField(
                      "license_categories"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "license_categories"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "license_categories",
                        value
                      );
                    }}
                    formatter={(
                      value
                    ) =>
                      value.toUpperCase()
                    }
                  />

                  <TextInfoCard
                    label={t(
                      "fields.tachoCardNumber.label"
                    )}
                    icon={<FaIdCard />}
                    value={
                      driver.tacho_card_number
                    }
                    editing={isEditingField(
                      "tacho_card_number"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "tacho_card_number"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "tacho_card_number",
                        value
                      );
                    }}
                  />

                  <DateInfoCard
                    label={t(
                      "fields.tachoIssueDate.label"
                    )}
                    icon={
                      <FaCalendarAlt />
                    }
                    value={
                      driver.issue_date_tacho_card
                    }
                    editing={isEditingField(
                      "issue_date_tacho_card"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "issue_date_tacho_card"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "issue_date_tacho_card",
                        value
                      );
                    }}
                  />

                  <DateInfoCard
                    label={t(
                      "fields.tachoExpiryDate.label"
                    )}
                    icon={
                      <FaCalendarAlt />
                    }
                    value={
                      driver.expiry_date_tacho_card
                    }
                    editing={isEditingField(
                      "expiry_date_tacho_card"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "expiry_date_tacho_card"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "expiry_date_tacho_card",
                        value
                      );
                    }}
                  />
                </div>
              </DriverSection>

              <DriverSection
                title={t(
                  "sections.emergency"
                )}
              >
                <div className="edit-driver-infoGrid edit-driver-infoGrid-3">
                  <TextInfoCard
                    label={t(
                      "fields.emergencyName.label"
                    )}
                    icon={<FaUser />}
                    value={
                      driver.emergency_name
                    }
                    editing={isEditingField(
                      "emergency_name"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "emergency_name"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "emergency_name",
                        value
                      );
                    }}
                  />

                  <TextInfoCard
                    label={t(
                      "fields.emergencyContact.label"
                    )}
                    icon={
                      <FaPhoneAlt />
                    }
                    value={
                      driver.emergency_contact
                    }
                    editing={isEditingField(
                      "emergency_contact"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "emergency_contact"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "emergency_contact",
                        value
                      );
                    }}
                  />

                  <TextInfoCard
                    label={t(
                      "fields.emergencyFamily.label"
                    )}
                    icon={<FaUsers />}
                    value={
                      driver.emergency_family
                    }
                    editing={isEditingField(
                      "emergency_family"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "emergency_family"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "emergency_family",
                        value
                      );
                    }}
                  />
                </div>
              </DriverSection>

              <DriverSection
                title={t(
                  "sections.preferencesAvailability"
                )}
              >
                <div className="edit-driver-infoGrid edit-driver-infoGrid-3">
                  <ChipInfoCard
                    label={t(
                      "fields.languages.label"
                    )}
                    icon={
                      <FaLanguage />
                    }
                    values={
                      driver.languages_spoken
                    }
                    editing={isEditingField(
                      "languages_spoken"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "languages_spoken"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "languages_spoken",
                        value
                      );
                    }}
                    formatter={(
                      value
                    ) =>
                      value.toUpperCase()
                    }
                  />

                  <ChipInfoCard
                    label={t(
                      "fields.workAvailability.label"
                    )}
                    icon={
                      <FaCalendarAlt />
                    }
                    values={
                      driver.work_availability
                    }
                    editing={isEditingField(
                      "work_availability"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "work_availability"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "work_availability",
                        value
                      );
                    }}
                    formatter={(
                      value
                    ) =>
                      t(
                        `fields.workAvailabilityOptions.${value}`,
                        {
                          defaultValue:
                            value,
                        }
                      )
                    }
                  />

                  <TextInfoCard
                    label={t(
                      "fields.contactPreferences.label"
                    )}
                    icon={
                      <FaPhoneAlt />
                    }
                    value={
                      driver.contact_preferences
                    }
                    editing={isEditingField(
                      "contact_preferences"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "contact_preferences"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "contact_preferences",
                        value
                      );
                    }}
                  />

                  <TextInfoCard
                    label={t(
                      "fields.lanesPreference.label"
                    )}
                    icon={<FaRoute />}
                    value={
                      driver.lanes_preference
                    }
                    editing={isEditingField(
                      "lanes_preference"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "lanes_preference"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "lanes_preference",
                        value
                      );
                    }}
                  />

                  <TextInfoCard
                    label={t(
                      "fields.homeBaseStart.label"
                    )}
                    icon={
                      <FaMapMarkerAlt />
                    }
                    value={
                      driver.home_base_start
                    }
                    editing={isEditingField(
                      "home_base_start"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "home_base_start"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "home_base_start",
                        value
                      );
                    }}
                  />

                  <TextInfoCard
                    label={t(
                      "fields.homeBaseEnds.label"
                    )}
                    icon={
                      <FaMapMarkerAlt />
                    }
                    value={
                      driver.home_base_ends
                    }
                    editing={isEditingField(
                      "home_base_ends"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "home_base_ends"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "home_base_ends",
                        value
                      );
                    }}
                  />

                  <SelectInfoCard
                    t={t}
                    label={t(
                      "fields.takePhotos.label"
                    )}
                    icon={<FaCamera />}
                    value={boolToSelect(
                      driver.take_photos
                    )}
                    options={
                      yesNoOptions
                    }
                    editing={isEditingField(
                      "take_photos"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "take_photos"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "take_photos",
                        value ===
                          "true"
                      );
                    }}
                    statusStyle
                  />

                  <SelectInfoCard
                    t={t}
                    label={t(
                      "fields.gpsSharing.label"
                    )}
                    icon={
                      <FaMapMarkerAlt />
                    }
                    value={boolToSelect(
                      driver.gps_sharing
                    )}
                    options={
                      yesNoUnsetOptions
                    }
                    editing={isEditingField(
                      "gps_sharing"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "gps_sharing"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "gps_sharing",
                        boolFromSelect(
                          value
                        )
                      );
                    }}
                    statusStyle
                  />

                  <SelectInfoCard
                    t={t}
                    label={t(
                      "fields.urgentJobs.label"
                    )}
                    icon={<FaBolt />}
                    value={boolToSelect(
                      driver.urgent_jobs
                    )}
                    options={
                      yesNoOptions
                    }
                    editing={isEditingField(
                      "urgent_jobs"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "urgent_jobs"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "urgent_jobs",
                        value ===
                          "true"
                      );
                    }}
                    statusStyle
                  />
                </div>
              </DriverSection>

              <DriverSection
                title={t(
                  "sections.experienceCertifications"
                )}
              >
                <div className="edit-driver-infoGrid edit-driver-infoGrid-3">
                  <SelectInfoCard
                    t={t}
                    label={t(
                      "fields.hiab.label"
                    )}
                    icon={
                      <FaTruckLoading />
                    }
                    value={boolToSelect(
                      driver.crane_hiab_license
                    )}
                    options={
                      yesNoOptions
                    }
                    editing={isEditingField(
                      "crane_hiab_license"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "crane_hiab_license"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "crane_hiab_license",
                        value ===
                          "true"
                      );
                    }}
                    statusStyle
                  />

                  <SelectInfoCard
                    t={t}
                    label={t(
                      "fields.tanker.label"
                    )}
                    icon={<FaOilCan />}
                    value={boolToSelect(
                      driver.tanker_experience
                    )}
                    options={
                      yesNoOptions
                    }
                    editing={isEditingField(
                      "tanker_experience"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "tanker_experience"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "tanker_experience",
                        value ===
                          "true"
                      );
                    }}
                    statusStyle
                  />

                  <SelectInfoCard
                    t={t}
                    label={t(
                      "fields.refrigerated.label"
                    )}
                    icon={
                      <FaSnowflake />
                    }
                    value={boolToSelect(
                      driver.refrigerated_experience
                    )}
                    options={
                      yesNoOptions
                    }
                    editing={isEditingField(
                      "refrigerated_experience"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "refrigerated_experience"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "refrigerated_experience",
                        value ===
                          "true"
                      );
                    }}
                    statusStyle
                  />

                  <SelectInfoCard
                    t={t}
                    label={t(
                      "fields.specialTransport.label"
                    )}
                    icon={<FaStar />}
                    value={boolToSelect(
                      driver.special_transport_experience
                    )}
                    options={
                      yesNoOptions
                    }
                    editing={isEditingField(
                      "special_transport_experience"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "special_transport_experience"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "special_transport_experience",
                        value ===
                          "true"
                      );
                    }}
                    statusStyle
                  />

                  <SelectInfoCard
                    t={t}
                    label={t(
                      "fields.nightWeekend.label"
                    )}
                    icon={<FaMoon />}
                    value={boolToSelect(
                      driver.night_weekend_driving
                    )}
                    options={
                      yesNoOptions
                    }
                    editing={isEditingField(
                      "night_weekend_driving"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "night_weekend_driving"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "night_weekend_driving",
                        value ===
                          "true"
                      );
                    }}
                    statusStyle
                  />

                  <SelectInfoCard
                    t={t}
                    label={t(
                      "fields.sleepInTruck.label"
                    )}
                    icon={<FaBed />}
                    value={boolToSelect(
                      driver.sleep_in_truck
                    )}
                    options={
                      yesNoOptions
                    }
                    editing={isEditingField(
                      "sleep_in_truck"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "sleep_in_truck"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "sleep_in_truck",
                        value ===
                          "true"
                      );
                    }}
                    statusStyle
                  />

                  <SelectInfoCard
                    t={t}
                    label={t(
                      "fields.adr.label"
                    )}
                    icon={
                      <FaExclamationTriangle />
                    }
                    value={
                      driver.adr_hazmar_certification ||
                      "NAO"
                    }
                    options={
                      adrOptions
                    }
                    editing={isEditingField(
                      "adr_hazmar_certification"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "adr_hazmar_certification"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "adr_hazmar_certification",
                        value
                      );
                    }}
                    statusStyle
                  />

                  <DateInfoCard
                    label={t(
                      "fields.adrExpiry.label"
                    )}
                    icon={
                      <FaCalendarAlt />
                    }
                    value={
                      driver.expiry_date_adr_hazmat_certificate
                    }
                    editing={isEditingField(
                      "expiry_date_adr_hazmat_certificate"
                    )}
                    onEdit={() => {
                      startEditingField(
                        "expiry_date_adr_hazmat_certificate"
                      );
                    }}
                    onDone={
                      stopEditingField
                    }
                    onChange={(
                      value
                    ) => {
                      updateField(
                        "expiry_date_adr_hazmat_certificate",
                        value
                      );
                    }}
                  />
                </div>
              </DriverSection>

              <DriverSection
                title={t(
                  "sections.uploadDocuments"
                )}
              >
                <div className="edit-driver-docGrid">
                  <DocCard
                    t={t}
                    title={t(
                      "files.driverIdPhoto.label"
                    )}
                    field="driver_id_photo"
                    max={2}
                    selectedFiles={
                      fileInputs.driver_id_photo
                    }
                    getDocItemsWithPreview={
                      getDocItemsWithPreview
                    }
                    openDocViewer={
                      openDocViewer
                    }
                    handleDocFilesChange={
                      handleDocFilesChange
                    }
                  />

                  <DocCard
                    t={t}
                    title={t(
                      "files.driverLicensePhoto.label"
                    )}
                    field="driver_license_photo"
                    max={2}
                    selectedFiles={
                      fileInputs.driver_license_photo
                    }
                    getDocItemsWithPreview={
                      getDocItemsWithPreview
                    }
                    openDocViewer={
                      openDocViewer
                    }
                    handleDocFilesChange={
                      handleDocFilesChange
                    }
                  />

                  <DocCard
                    t={t}
                    title={t(
                      "files.tachoCardPhoto.label"
                    )}
                    field="tacho_card_photo"
                    max={2}
                    selectedFiles={
                      fileInputs.tacho_card_photo
                    }
                    getDocItemsWithPreview={
                      getDocItemsWithPreview
                    }
                    openDocViewer={
                      openDocViewer
                    }
                    handleDocFilesChange={
                      handleDocFilesChange
                    }
                  />

                  <DocCard
                    t={t}
                    title={t(
                      "files.adrHazmatCertificatePhoto.label"
                    )}
                    field="upload_photo_adr_hazmat_certificate"
                    max={2}
                    selectedFiles={
                      fileInputs.upload_photo_adr_hazmat_certificate
                    }
                    getDocItemsWithPreview={
                      getDocItemsWithPreview
                    }
                    openDocViewer={
                      openDocViewer
                    }
                    handleDocFilesChange={
                      handleDocFilesChange
                    }
                  />
                </div>
              </DriverSection>
            </div>

            <div className="edit-driver-footerActions">
              <div className="edit-driver-footerHint">
                {t(
                  "actions.securityHint",
                  {
                    defaultValue:
                      "As suas alterações serão guardadas em segurança.",
                  }
                )}
              </div>

              <div className="edit-driver-footerBtns">
                <button
                  type="button"
                  className="edit-driver-cancel-btn"
                  disabled={
                    isSaving
                  }
                  onClick={() => {
                    requestNavigation(
                      "/drivers"
                    );
                  }}
                >
                  {t(
                    "actions.cancel"
                  )}
                </button>

                <button
                  type="submit"
                  className="edit-driver-save-btn"
                  disabled={
                    isSaving
                  }
                >
                  <FaSave />

                  <span>
                    {isSaving
                      ? t(
                          "actions.saving"
                        )
                      : t(
                          "actions.save"
                        )}
                  </span>
                </button>
              </div>
            </div>

            {profilePhotoModalOpen && (
              <div
                className="edit-driver-profileModal-overlay"
                role="dialog"
                aria-modal="true"
                aria-label={t(
                  "profile.modalTitle",
                  {
                    defaultValue:
                      "Foto de perfil",
                  }
                )}
                onClick={
                  closeProfilePhotoModal
                }
              >
                <div
                  className="edit-driver-profileModal"
                  onClick={(
                    event
                  ) => {
                    event.stopPropagation();
                  }}
                >
                  <div className="edit-driver-profileModal-header">
                    <div>
                      <h3>
                        {t(
                          "profile.modalTitle",
                          {
                            defaultValue:
                              "Foto de perfil",
                          }
                        )}
                      </h3>

                      <p>
                        {profileEditorSource
                          ? t(
                              "profile.modalSubtitle",
                              {
                                defaultValue:
                                  "Arraste a imagem para ajustar o enquadramento e utilize o zoom.",
                              }
                            )
                          : t(
                              "profile.modalEmptySubtitle",
                              {
                                defaultValue:
                                  "Escolha uma fotografia para o motorista.",
                              }
                            )}
                      </p>
                    </div>

                    <button
                      type="button"
                      className="edit-driver-profileModal-close"
                      onClick={
                        closeProfilePhotoModal
                      }
                      aria-label={t(
                        "files.actions.closeTitle"
                      )}
                      title={t(
                        "files.actions.closeTitle"
                      )}
                    >
                      ✕
                    </button>
                  </div>

                  <div className="edit-driver-profileModal-body">
                    {profileEditorSource ? (
                      <>
                        <div
                          ref={
                            profileCropStageRef
                          }
                          className={[
                            "edit-driver-profileEditor-stage",
                            isProfileDragging
                              ? "is-dragging"
                              : "",
                          ]
                            .filter(
                              Boolean
                            )
                            .join(
                              " "
                            )}
                          onPointerDown={
                            handleProfileEditorPointerDown
                          }
                          onPointerMove={
                            handleProfileEditorPointerMove
                          }
                          onPointerUp={
                            stopProfileEditorDrag
                          }
                          onPointerCancel={
                            stopProfileEditorDrag
                          }
                        >
                          <img
                            src={
                              profileEditorSource
                            }
                            alt={t(
                              "profile.alt"
                            )}
                            className="edit-driver-profileEditor-img"
                            draggable={
                              false
                            }
                            onLoad={(
                              event
                            ) => {
                              setProfileEditorImageSize({
                                width:
                                  event.currentTarget
                                    .naturalWidth,

                                height:
                                  event.currentTarget
                                    .naturalHeight,
                              });

                              setProfileEditorZoom(
                                1
                              );

                              setProfileEditorOffset({
                                x: 0,
                                y: 0,
                              });
                            }}
                            style={{
                              width:
                                profileEditorImageSize.width &&
                                profileEditorImageSize.height
                                  ? profileEditorImageSize.width >=
                                    profileEditorImageSize.height
                                    ? "auto"
                                    : "100%"
                                  : "100%",

                              height:
                                profileEditorImageSize.width &&
                                profileEditorImageSize.height
                                  ? profileEditorImageSize.width >=
                                    profileEditorImageSize.height
                                    ? "100%"
                                    : "auto"
                                  : "100%",

                              transform: `translate(-50%, -50%) translate(${profileEditorOffset.x}px, ${profileEditorOffset.y}px) scale(${profileEditorZoom})`,
                            }}
                          />

                          <div className="edit-driver-profileEditor-mask" />
                        </div>

                        <div className="edit-driver-profileEditor-help">
                          {t(
                            "profile.dragHint",
                            {
                              defaultValue:
                                "Arraste a fotografia para escolher a área visível.",
                            }
                          )}
                        </div>

                        <div className="edit-driver-profileEditor-controls">
                          <div className="edit-driver-profileEditor-zoomRow">
                            <span>
                              {t(
                                "profile.zoom",
                                {
                                  defaultValue:
                                    "Zoom",
                                }
                              )}
                            </span>

                            <input
                              type="range"
                              min="1"
                              max="3"
                              step="0.01"
                              value={
                                profileEditorZoom
                              }
                              onChange={(
                                event
                              ) => {
                                const nextZoom =
                                  Number(
                                    event.target
                                      .value
                                  );

                                setProfileEditorZoom(
                                  nextZoom
                                );

                                setProfileEditorOffset(
                                  (
                                    previousOffset
                                  ) =>
                                    clampProfileEditorOffset(
                                      previousOffset,
                                      nextZoom
                                    )
                                );
                              }}
                            />

                            <strong>
                              {Math.round(
                                profileEditorZoom *
                                  100
                              )}
                              %
                            </strong>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="edit-driver-profileEditor-empty">
                        <div className="edit-driver-profileEditor-emptyIcon">
                          <FaCamera />
                        </div>

                        <strong>
                          {t(
                            "profile.noPhoto",
                            {
                              defaultValue:
                                "Sem foto de perfil",
                            }
                          )}
                        </strong>

                        <span>
                          {t(
                            "profile.noPhotoHint",
                            {
                              defaultValue:
                                "Escolha uma imagem para começar.",
                            }
                          )}
                        </span>
                      </div>
                    )}

                    {profileEditorError && (
                      <div className="edit-driver-profileEditor-error">
                        {profileEditorError}
                      </div>
                    )}
                  </div>

                  <div className="edit-driver-profileModal-footer">
                    <div className="edit-driver-profileModal-footerLeft">
                      {profileEditorSource && (
                        <button
                          type="button"
                          className="edit-driver-profileModal-secondary"
                          onClick={
                            resetProfileEditor
                          }
                        >
                          {t(
                            "profile.reset",
                            {
                              defaultValue:
                                "Repor enquadramento",
                            }
                          )}
                        </button>
                      )}

                      <button
                        type="button"
                        className="edit-driver-profileModal-secondary"
                        onClick={() => {
                          profilePhotoInputRef.current?.click();
                        }}
                      >
                        <FaUpload />

                        <span>
                          {profileEditorSource
                            ? t(
                                "profile.chooseAnother",
                                {
                                  defaultValue:
                                    "Escolher outra",
                                }
                              )
                            : t(
                                "profile.choosePhoto"
                              )}
                        </span>
                      </button>
                    </div>

                    <div className="edit-driver-profileModal-footerRight">
                      <button
                        type="button"
                        className="edit-driver-profileModal-secondary"
                        disabled={
                          isApplyingProfileCrop
                        }
                        onClick={
                          closeProfilePhotoModal
                        }
                      >
                        {t(
                          "actions.cancel"
                        )}
                      </button>

                      {profileEditorSource && (
                        <button
                          type="button"
                          className="edit-driver-profileModal-primary"
                          disabled={
                            isApplyingProfileCrop
                          }
                          onClick={
                            handleApplyProfilePhoto
                          }
                        >
                          <FaCheck />

                          <span>
                            {isApplyingProfileCrop
                              ? t(
                                  "profile.applying",
                                  {
                                    defaultValue:
                                      "A aplicar...",
                                  }
                                )
                              : t(
                                  "profile.apply",
                                  {
                                    defaultValue:
                                      "Aplicar foto",
                                  }
                                )}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {unsavedChangesModalOpen && (
              <div
                className="edit-driver-unsaved-overlay"
                role="presentation"
                onMouseDown={(event) => {
                  if (
                    event.target ===
                    event.currentTarget
                  ) {
                    handleContinueEditing();
                  }
                }}
              >
                <div
                  className="edit-driver-unsaved-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="edit-driver-unsaved-title"
                  onMouseDown={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <div className="edit-driver-unsaved-icon">
                    <FaExclamationTriangle />
                  </div>

                  <div className="edit-driver-unsaved-content">
                    <h2 id="edit-driver-unsaved-title">
                      {t(
                        "unsavedChanges.title",
                        {
                          defaultValue:
                            "Alterações por guardar",
                        }
                      )}
                    </h2>

                    <p>
                      {t(
                        "unsavedChanges.message",
                        {
                          defaultValue:
                            "Existem alterações que ainda não foram guardadas. O que pretende fazer?",
                        }
                      )}
                    </p>
                  </div>

                  <div className="edit-driver-unsaved-actions">
                    <button
                      type="button"
                      className="edit-driver-unsaved-continue"
                      disabled={
                        isSaving
                      }
                      onClick={
                        handleContinueEditing
                      }
                    >
                      {t(
                        "unsavedChanges.continueEditing",
                        {
                          defaultValue:
                            "Continuar a editar",
                        }
                      )}
                    </button>

                    <button
                      type="button"
                      className="edit-driver-unsaved-save"
                      disabled={
                        isSaving
                      }
                      onClick={
                        handleSaveAndLeave
                      }
                    >
                      <FaSave />

                      <span>
                        {isSaving
                          ? t(
                              "actions.saving"
                            )
                          : t(
                              "unsavedChanges.saveAndLeave",
                              {
                                defaultValue:
                                  "Guardar e sair",
                              }
                            )}
                      </span>
                    </button>

                    <button
                      type="button"
                      className="edit-driver-unsaved-leave"
                      disabled={
                        isSaving
                      }
                      onClick={
                        handleLeaveWithoutSaving
                      }
                    >
                      {t(
                        "unsavedChanges.leaveWithoutSaving",
                        {
                          defaultValue:
                            "Sair sem guardar",
                        }
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {docViewerOpen && (
              <div
                className="edit-driver-docModal-overlay"
                role="dialog"
                aria-modal="true"
                onClick={
                  closeDocViewer
                }
              >
                <div
                  className="edit-driver-docModal"
                  onClick={(
                    event
                  ) => {
                    event.stopPropagation();
                  }}
                >
                  <div className="edit-driver-docModal-top">
                    <div className="edit-driver-docModal-title">
                      <span className="edit-driver-docModal-titleText">
                        {t(
                          "files.viewer"
                        )}
                      </span>

                      <span
                        className="edit-driver-docModal-fileName"
                        title={prettyNameFromUrl(
                          docViewerItems[
                            docViewerIndex
                          ]
                        )}
                      >
                        {prettyNameFromUrl(
                          docViewerItems[
                            docViewerIndex
                          ]
                        ) ||
                          t(
                            "files.viewerFallbackDoc"
                          )}
                      </span>

                      <span className="edit-driver-docModal-counter">
                        {docViewerIndex +
                          1}
                        /
                        {
                          docViewerItems.length
                        }
                      </span>
                    </div>

                    <div className="edit-driver-docModal-actions">
                      <button
                        type="button"
                        className="edit-driver-docModal-btn"
                        onClick={() => {
                          window.open(
                            docViewerItems[
                              docViewerIndex
                            ],
                            "_blank"
                          );
                        }}
                      >
                        {t(
                          "files.actions.openInNewTab"
                        )}
                      </button>

                      <button
                        type="button"
                        className="edit-driver-docModal-btn close"
                        onClick={
                          closeDocViewer
                        }
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <div className="edit-driver-docModal-body">
                    <button
                      type="button"
                      className="edit-driver-docModal-nav left"
                      disabled={
                        docViewerIndex ===
                        0
                      }
                      onClick={
                        goPrev
                      }
                    >
                      ‹
                    </button>

                    <div className="edit-driver-docModal-stage">
                      {isPdfUrl(
                        docViewerItems[
                          docViewerIndex
                        ]
                      ) ? (
                        <iframe
                          title="PDF"
                          className="edit-driver-docModal-iframe"
                          src={
                            docViewerItems[
                              docViewerIndex
                            ]
                          }
                        />
                      ) : (
                        <img
                          className="edit-driver-docModal-img"
                          src={
                            docViewerItems[
                              docViewerIndex
                            ]
                          }
                          alt={t(
                            "files.viewerImageAlt"
                          )}
                          draggable={
                            false
                          }
                          onDoubleClick={(
                            event
                          ) => {
                            const stage =
                              event.currentTarget.closest(
                                ".edit-driver-docModal-stage"
                              );

                            if (
                              stage
                            ) {
                              stage.classList.toggle(
                                "zoomed"
                              );
                            }
                          }}
                        />
                      )}
                    </div>

                    <button
                      type="button"
                      className="edit-driver-docModal-nav right"
                      disabled={
                        docViewerIndex ===
                        docViewerItems.length -
                          1
                      }
                      onClick={
                        goNext
                      }
                    >
                      ›
                    </button>
                  </div>
                </div>
              </div>
            )}
          </main>
        </form>
      </div>
    </div>
  );
}

export default EditDriver;